import { useState, useEffect, useRef, useCallback } from "react";

// ── HOOK MOBILE ──────────────────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ── DATOS INICIALES ──────────────────────────────────────────────
const VENDEDORES = [
  { id: "admin", nombre: "Admin",   pin: "1234", rol: "admin"    },
  { id: "v1",    nombre: "Eitán",   pin: "1122", rol: "vendedor" },
  { id: "v2",    nombre: "Agustín", pin: "1133", rol: "vendedor" },
];

const REGIONES_INICIALES = ["CABA", "Córdoba", "Rosario", "Mendoza", "Santa Fe"];
const EMPRESAS_CAPTIO = ["Todas", "QZ SRL", "Quick Clean", "Air Quality"];

const COLUMNAS = [
  { id: "Nuevo",     label: "Nuevo",     color: "#6B7280", bg: "rgba(107,114,128,0.10)" },
  { id: "Validado",  label: "Validado",  color: "#3B82F6", bg: "rgba(59,130,246,0.10)"  },
  { id: "Cotizado",  label: "Cotizado",  color: "#F59E0B", bg: "rgba(245,158,11,0.10)"  },
  { id: "Ganado",    label: "Ganado",    color: "#10B981", bg: "rgba(16,185,129,0.10)"  },
  { id: "Perdido",   label: "Perdido",   color: "#EF4444", bg: "rgba(239,68,68,0.10)"   },
];

const FUENTE_ICON = {
  "Google Maps": "📍", "Páginas Amarillas": "📋", "Guía Oleo": "📋",
  "Cylex": "📋", "SerpAPI": "🔍", "Formulario Shopify": "🛍️",
  "Manual": "📁", "Google Places": "🗺️",
};

// ── HELPERS ──────────────────────────────────────────────────────
const fmt$ = v => v ? `$${Number(v).toLocaleString("es-AR")}` : "—";
const scoreColor = s => s >= 85 ? "#10B981" : s >= 68 ? "#F59E0B" : "#EF4444";

function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => {
    try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : initial; }
    catch { return initial; }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  return [val, setVal];
}

// Hook para cargar leads reales — nunca muestra datos demo, solo real o vacío
function useLeadsFromFile(setLeads, setLeadsListo) {
  useEffect(() => {
    fetch("https://qzsrl.velqo.io/api/leads")
      .then(r => {
        if (!r.ok) throw new Error("Sin servidor");
        return r.json();
      })
      .then(data => {
        if (!Array.isArray(data)) return;
        const normalized = data.map((l, i) => ({
          id:       l.id || i + 1,
          empresa:  l.empresa || "",
          contacto: l.contacto || l.nombre_contacto || "",
          cargo:    l.cargo_decisor || l.cargo || "",
          email:    l.email_estimado || l.email || "",
          tel:      l.telefono || l.tel || "",
          ciudad:   l.ciudad || "",
          region:   l.region || "CABA",
          segmento: l.segmento || "",
          fuente:   l.fuente || "Agente IA",
          estado:   l.estado || "Nuevo",
          vendedor: l.vendedor || "",
          score:    Number(l.score) || 70,
          monto:    Number(l.monto) || 0,
          fecha:    l.fecha || new Date().toISOString().split("T")[0],
          notas:          l.notas || "",
          empresa_captio: l.empresa_captio || "QZ SRL",
        }));
        setLeads(normalized);
      })
      .catch(err => {
        console.warn("Server no disponible:", err);
      })
      .finally(() => setLeadsListo(true));
  }, []);
}

// ── COMPONENTES BASE ─────────────────────────────────────────────
function ScoreBar({ score }) {
  const c = scoreColor(score);
  return (
    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
      <div style={{ flex:1, height:3, background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
        <div style={{ width:`${score}%`, height:"100%", background:c, borderRadius:2 }}/>
      </div>
      <span style={{ fontSize:10, color:c, fontWeight:700, minWidth:22 }}>{score}</span>
    </div>
  );
}

// ── MODAL MOVER LEAD ─────────────────────────────────────────────
function ModalMover({ lead, destino, onConfirm, onCancel }) {
  const [monto, setMonto] = useState(lead.monto || "");
  const [motivo, setMotivo] = useState("");

  const necesitaMonto  = destino === "Cotizado" || destino === "Ganado";
  const necesitaMotivo = destino === "Perdido";

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
    }}>
      <div style={{
        background:"#111827", border:"1px solid rgba(255,255,255,0.1)",
        borderRadius:16, padding:28, width:"100%", maxWidth:400,
      }}>
        <div style={{ fontSize:16, fontWeight:700, color:"#fff", marginBottom:6 }}>
          Mover a <span style={{ color: COLUMNAS.find(c=>c.id===destino)?.color }}>{destino}</span>
        </div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.5)", marginBottom:20 }}>
          {lead.empresa}
        </div>

        {necesitaMonto && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              {destino === "Cotizado" ? "Monto del presupuesto (ARS)" : "Monto del cierre (ARS)"}
            </label>
            <input
              type="number"
              value={monto}
              onChange={e => setMonto(e.target.value)}
              placeholder="ej: 150000"
              style={{
                width:"100%", marginTop:6, background:"rgba(255,255,255,0.06)",
                border:"1px solid rgba(255,255,255,0.12)", borderRadius:8,
                padding:"10px 14px", color:"#fff", fontSize:14, outline:"none", boxSizing:"border-box",
              }}
            />
          </div>
        )}

        {necesitaMotivo && (
          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:11, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1 }}>
              Motivo de pérdida
            </label>
            <select
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              style={{
                width:"100%", marginTop:6, background:"#1F2937",
                border:"1px solid rgba(255,255,255,0.12)", borderRadius:8,
                padding:"10px 14px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            >
              <option value="">Seleccionar motivo...</option>
              <option value="Precio">Precio — eligió otro proveedor más barato</option>
              <option value="Sin respuesta">Sin respuesta — no contestó</option>
              <option value="No calificó">No calificó — no era el perfil</option>
              <option value="Competencia">Competencia — eligió competidor</option>
              <option value="Postergado">Postergado — no es el momento</option>
              <option value="Otro">Otro motivo</option>
            </select>
          </div>
        )}

        <div style={{ display:"flex", gap:10, marginTop:8 }}>
          <button onClick={onCancel} style={{
            flex:1, padding:"10px 0", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
            background:"transparent", color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:13,
          }}>Cancelar</button>
          <button
            onClick={() => onConfirm({ monto: Number(monto) || 0, motivo })}
            disabled={necesitaMotivo && !motivo}
            style={{
              flex:2, padding:"10px 0", borderRadius:8, border:"none",
              background: COLUMNAS.find(c=>c.id===destino)?.color || "#FF6B00",
              color:"#fff", cursor:"pointer", fontSize:13, fontWeight:700,
              opacity: (necesitaMotivo && !motivo) ? 0.4 : 1,
            }}
          >Confirmar</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL DETALLE LEAD ───────────────────────────────────────────
function ModalDetalle({ lead, vendedores, onClose, onAsignar, esAdmin, usuarioActual }) {
  const [mostrarPresupuesto, setMostrarPresupuesto] = useState(false);
  const [enviandoEmail, setEnviandoEmail]           = useState(null);
  const [emailLog, setEmailLog]                     = useState([]);
  const [loadingLog, setLoadingLog]                 = useState(false);
  const [descPresupuesto, setDescPresupuesto]       = useState("");
  const [montoNeto, setMontoNeto]                   = useState("");
  const [generando, setGenerando]                   = useState(false);
  const [pdfResult, setPdfResult]                   = useState(null);

  const empKey = {
    "QZ SRL":      "qzsrl",
    "Quick Clean": "lowcost",
    "Air Quality": "airquality",
  }[lead.empresa_captio] || "qzsrl";

  // Cargar historial de emails
  useEffect(() => {
    setLoadingLog(true);
    fetch(`https://qzsrl.velqo.io/api/actividad?lead_id=${lead.id}&limit=20`)
      .then(r => r.json())
      .then(data => { setEmailLog(Array.isArray(data) ? data : []); setLoadingLog(false); })
      .catch(() => setLoadingLog(false));
  }, [lead.id]);

  const enviarEmail = (tipo) => {
    const labels = { presentacion:"email de presentación", "5preguntas":"email con 5 preguntas" };
    if (!window.confirm(`¿Confirmar envío de ${labels[tipo]||tipo} a ${lead.empresa}?`)) return;
    setEnviandoEmail(tipo);
    fetch(`https://qzsrl.velqo.io/api/email/${tipo}/${empKey}/${lead.id}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ enviado_por: usuarioActual?.id || "sistema" }),
    })
    .then(r => r.json())
    .then(d => {
      setEnviandoEmail(null);
      if (d.ok) {
        alert("✅ Email enviado correctamente");
        fetch(`https://qzsrl.velqo.io/api/actividad?lead_id=${lead.id}&limit=20`)
          .then(r => r.json())
          .then(data => setEmailLog(Array.isArray(data) ? data : []))
          .catch(() => {});
      } else {
        alert(`❌ Error: ${d.error}`);
      }
    })
    .catch(() => { setEnviandoEmail(null); alert("❌ Error de conexión"); });
  };

  const vendedor = vendedores.find(v => v.id === lead.vendedor);

  const generarPDF = () => {
    if (!descPresupuesto || !montoNeto) return alert("Completá la descripción y el monto");
    setGenerando(true);
    fetch("https://qzsrl.velqo.io/api/presupuesto", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        empresa_key:     empKey,
        lead_id:         lead.id,
        descripcion:     descPresupuesto,
        monto_neto:      parseFloat(montoNeto),
        vendedor_nombre: vendedor ? vendedor.nombre : "Equipo Comercial",
        vendedor_email:  "contacto@qzsrl.com.ar",
      }),
    })
    .then(r => r.json())
    .then(d => {
      setGenerando(false);
      if (d.ok) setPdfResult(d);
      else alert("Error: " + d.error);
    })
    .catch(() => { setGenerando(false); alert("Error al generar"); });
  };

  const iva        = montoNeto ? Math.round(parseFloat(montoNeto) * 0.21 * 100) / 100 : 0;
  const total      = montoNeto ? Math.round((parseFloat(montoNeto) + iva) * 100) / 100 : 0;
  const pdfUrl     = pdfResult ? `https://qzsrl.velqo.io${pdfResult.url}` : "";
  const waMsg      = pdfResult ? encodeURIComponent(`Hola ${lead.contacto || lead.empresa}, le adjuntamos el presupuesto N° ${pdfResult.numero} por $${pdfResult.total.toLocaleString()}. Puede descargarlo aquí: ${pdfUrl}`) : "";
  const waLink     = lead.tel ? `https://wa.me/${lead.tel.replace(/\D/g,"")}?text=${waMsg}` : `https://wa.me/?text=${waMsg}`;

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,0.7)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16,
    }}>
      <div style={{
        background:"#111827", border:"1px solid rgba(255,255,255,0.1)",
        borderRadius:16, padding:28, width:"100%", maxWidth:480, maxHeight:"90vh", overflowY:"auto",
      }}>
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:20 }}>
          <div>
            <div style={{ fontSize:17, fontWeight:800, color:"#fff" }}>{lead.empresa}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginTop:2 }}>{lead.segmento} · {lead.region}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:20 }}>✕</button>
        </div>

        {[
          ["Contacto", lead.contacto],
          ["Cargo", lead.cargo],
          ["Email", lead.email],
          ["Teléfono", lead.tel],
          ["Ciudad", lead.ciudad],
          ["Fuente", lead.fuente],
          ["Fecha", lead.fecha],
          ["Notas", lead.notas],
          lead.monto ? ["Monto", fmt$(lead.monto)] : null,
        ].filter(Boolean).map(([k,v]) => v ? (
          <div key={k} style={{ display:"flex", gap:12, marginBottom:10 }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", minWidth:70, textTransform:"uppercase", letterSpacing:.5, paddingTop:1 }}>{k}</span>
            <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)", flex:1 }}>{v}</span>
          </div>
        ) : null)}

        <div style={{ marginTop:8, marginBottom:16 }}>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:.5 }}>Score</span>
          <div style={{ marginTop:6 }}><ScoreBar score={lead.score}/></div>
        </div>

        {esAdmin && (
          <div>
            <label style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:.5 }}>Asignar a vendedor</label>
            <select
              value={lead.vendedor || ""}
              onChange={e => onAsignar(lead.id, e.target.value)}
              style={{
                width:"100%", marginTop:6, background:"#1F2937",
                border:"1px solid rgba(255,255,255,0.12)", borderRadius:8,
                padding:"10px 14px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            >
              <option value="">Sin asignar</option>
              {vendedores.filter(v=>v.rol==="vendedor").map(v =>
                <option key={v.id} value={v.id}>{v.nombre}</option>
              )}
            </select>
          </div>
        )}

        {/* ── ENVÍO DE EMAILS ── */}
        <div style={{ marginTop:20, borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:16 }}>
          <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:.5, marginBottom:10 }}>
            Envío de emails
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            <button onClick={() => enviarEmail("presentacion")} disabled={!!enviandoEmail} style={{
              flex:1, padding:"9px 0", borderRadius:8, border:"none",
              background: enviandoEmail==="presentacion" ? "rgba(255,255,255,0.05)" : "rgba(59,130,246,0.15)",
              color: enviandoEmail==="presentacion" ? "#6B7280" : "#3B82F6",
              fontWeight:700, fontSize:12, cursor: enviandoEmail ? "not-allowed" : "pointer",
            }}>
              {enviandoEmail==="presentacion" ? "⏳ Enviando..." : "✉ Presentación"}
            </button>
            <button onClick={() => enviarEmail("5preguntas")} disabled={!!enviandoEmail} style={{
              flex:1, padding:"9px 0", borderRadius:8, border:"none",
              background: enviandoEmail==="5preguntas" ? "rgba(255,255,255,0.05)" : "rgba(139,92,246,0.15)",
              color: enviandoEmail==="5preguntas" ? "#6B7280" : "#8B5CF6",
              fontWeight:700, fontSize:12, cursor: enviandoEmail ? "not-allowed" : "pointer",
            }}>
              {enviandoEmail==="5preguntas" ? "⏳ Enviando..." : "❓ 5 Preguntas"}
            </button>
          </div>

          {/* Historial de emails */}
          {loadingLog && <div style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>Cargando historial...</div>}
          {!loadingLog && emailLog.length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.25)", marginBottom:6 }}>Historial de contacto</div>
              {emailLog.map(log => {
                const tipoLabel = { presentacion:"Presentación", "5preguntas":"5 Preguntas", presupuesto:"Presupuesto" }[log.tipo] || log.tipo;
                const fecha = new Date(log.creado_en).toLocaleString("es-AR", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" });
                return (
                  <div key={log.id} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"5px 8px", borderRadius:6, marginBottom:4,
                    background: log.estado==="enviado" ? "rgba(16,185,129,0.07)" : "rgba(239,68,68,0.07)",
                    border:`1px solid ${log.estado==="enviado" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)"}`,
                  }}>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <span style={{ fontSize:10, color: log.estado==="enviado" ? "#10B981" : "#EF4444" }}>
                        {log.estado==="enviado" ? "✓" : "✗"}
                      </span>
                      <span style={{ fontSize:11, color:"rgba(255,255,255,0.65)" }}>{tipoLabel}</span>
                      {log.enviado_por && log.enviado_por !== "sistema" && (
                        <span style={{ fontSize:9, color:"rgba(255,255,255,0.3)" }}>· {log.enviado_por}</span>
                      )}
                    </div>
                    <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{fecha}</span>
                  </div>
                );
              })}
            </div>
          )}
          {!loadingLog && emailLog.length === 0 && (
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.2)", textAlign:"center", padding:"6px 0", marginBottom:8 }}>
              Sin emails enviados aún
            </div>
          )}
        </div>

        {/* ── PRESUPUESTO ── */}
        <div style={{ marginTop:12, borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:16 }}>
          <button onClick={() => { setMostrarPresupuesto(!mostrarPresupuesto); setPdfResult(null); }} style={{
            width:"100%", padding:"10px", borderRadius:8, border:"none",
            background: mostrarPresupuesto ? "rgba(255,107,0,0.2)" : "rgba(255,107,0,0.1)",
            color:"#FF6B00", fontWeight:700, fontSize:13, cursor:"pointer",
          }}>
            📄 {mostrarPresupuesto ? "Cerrar presupuesto" : "Generar presupuesto"}
          </button>

          {mostrarPresupuesto && !pdfResult && (
            <div style={{ marginTop:14 }}>
              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:.5, display:"block", marginBottom:6 }}>
                  Descripción del servicio
                </label>
                <textarea
                  value={descPresupuesto}
                  onChange={e => setDescPresupuesto(e.target.value)}
                  placeholder="Ej: Limpieza técnica de campanas extractoras y ductos de grasa. Incluye desmontaje, limpieza química, desinfección y certificado digital."
                  rows={4}
                  style={{
                    width:"100%", background:"#1F2937", border:"1px solid rgba(255,255,255,0.12)",
                    borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:12,
                    outline:"none", resize:"vertical", boxSizing:"border-box",
                  }}
                />
              </div>

              <div style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:.5, display:"block", marginBottom:6 }}>
                  Monto neto (sin IVA)
                </label>
                <input
                  type="number"
                  value={montoNeto}
                  onChange={e => setMontoNeto(e.target.value)}
                  placeholder="Ej: 45000"
                  style={{
                    width:"100%", background:"#1F2937", border:"1px solid rgba(255,255,255,0.12)",
                    borderRadius:8, padding:"10px 12px", color:"#fff", fontSize:13,
                    outline:"none", boxSizing:"border-box",
                  }}
                />
              </div>

              {montoNeto > 0 && (
                <div style={{ background:"rgba(255,107,0,0.08)", border:"1px solid rgba(255,107,0,0.2)", borderRadius:8, padding:"10px 14px", marginBottom:14 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"rgba(255,255,255,0.5)", marginBottom:4 }}>
                    <span>Subtotal</span><span>${parseFloat(montoNeto).toLocaleString()}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, color:"rgba(255,255,255,0.5)", marginBottom:6 }}>
                    <span>IVA 21%</span><span>${iva.toLocaleString()}</span>
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", fontSize:14, fontWeight:800, color:"#FF6B00", borderTop:"1px solid rgba(255,107,0,0.2)", paddingTop:6 }}>
                    <span>TOTAL</span><span>${total.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <button onClick={generarPDF} disabled={generando} style={{
                width:"100%", padding:"12px", borderRadius:8, border:"none",
                background: generando ? "#374151" : "#FF6B00",
                color: generando ? "#9CA3AF" : "#0F0A00",
                fontWeight:700, fontSize:13, cursor: generando ? "not-allowed" : "pointer",
              }}>
                {generando ? "⏳ Generando PDF..." : "📄 Generar PDF"}
              </button>
            </div>
          )}

          {pdfResult && (
            <div style={{ marginTop:14 }}>
              <div style={{ background:"rgba(16,185,129,0.1)", border:"1px solid rgba(16,185,129,0.3)", borderRadius:8, padding:"12px 14px", marginBottom:12, textAlign:"center" }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#10B981", marginBottom:2 }}>✅ Presupuesto generado</div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>N° {pdfResult.numero} · Total ${pdfResult.total?.toLocaleString()}</div>
              </div>

              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <a href={pdfUrl} target="_blank" rel="noreferrer" style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(255,255,255,0.08)", color:"#fff", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>📥 Descargar PDF</a>

                <a href={`mailto:${lead.email}?subject=Presupuesto N° ${pdfResult.numero} — QZ SRL&body=Estimado ${lead.contacto || lead.empresa},%0D%0A%0D%0AAdjuntamos el presupuesto solicitado.%0D%0ADescargue el PDF aquí: ${pdfUrl}%0D%0A%0D%0ASaludos,%0D%0AEquipo QZ SRL`} style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(59,130,246,0.15)", color:"#3B82F6", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>📧 Enviar por email</a>

                <a href={waLink} target="_blank" rel="noreferrer" style={{
                  display:"block", textAlign:"center", padding:"10px", borderRadius:8,
                  background:"rgba(37,211,102,0.15)", color:"#25D366", textDecoration:"none",
                  fontSize:13, fontWeight:600,
                }}>💬 Enviar por WhatsApp</a>

                <button onClick={() => { setPdfResult(null); setDescPresupuesto(""); setMontoNeto(""); }} style={{
                  padding:"8px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
                  background:"transparent", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:12,
                }}>Nuevo presupuesto</button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// ── CARD LEAD ────────────────────────────────────────────────────
function LeadCard({ lead, columnaColor, onDragStart, onTap, esAdmin, vendedores }) {
  const vendedor = vendedores.find(v => v.id === lead.vendedor);
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, lead.id)}
      onClick={() => onTap(lead)}
      style={{
        background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.07)",
        borderLeft:`3px solid ${columnaColor}`, borderRadius:10, padding:"12px 14px",
        cursor:"grab", marginBottom:8, transition:"transform .15s, box-shadow .15s",
        userSelect:"none",
      }}
      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-1px)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(0,0,0,0.3)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}
    >
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
        {(() => {
          const empColors = {
            "QZ SRL":      { bg:"rgba(255,107,0,0.15)",  color:"#FF6B00" },
            "Quick Clean": { bg:"rgba(245,158,11,0.15)", color:"#F59E0B" },
            "Air Quality": { bg:"rgba(6,182,212,0.15)",  color:"#06B6D4" },
          };
          const ec = empColors[lead.empresa_captio] || { bg:"rgba(139,92,246,0.15)", color:"#8B5CF6" };
          return (
            <span style={{
              fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
              background:ec.bg, color:ec.color, whiteSpace:"nowrap",
            }}>{lead.empresa_captio || "QZ SRL"}</span>
          );
        })()}
      </div>
      <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:2, lineHeight:1.3 }}>{lead.empresa}</div>
      <div style={{ fontSize:11, color:"rgba(255,255,255,0.45)", marginBottom:8 }}>{lead.contacto} · {lead.cargo}</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{FUENTE_ICON[lead.fuente] || "📌"} {lead.fuente}</span>
        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{lead.region}</span>
      </div>
      <ScoreBar score={lead.score}/>
      {lead.monto > 0 && (
        <div style={{ marginTop:6, fontSize:12, fontWeight:700, color:"#F59E0B" }}>{fmt$(lead.monto)}</div>
      )}
      {esAdmin && vendedor && (
        <div style={{ marginTop:6, fontSize:10, color:"rgba(255,255,255,0.3)" }}>👤 {vendedor.nombre}</div>
      )}
    </div>
  );
}

// ── COLUMNA KANBAN ───────────────────────────────────────────────
function Columna({ col, leads, onDrop, onDragOver, onDragStart, onTap, esAdmin, vendedores }) {
  const total$ = leads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0);
  return (
    <div
      style={{ flex:1, minWidth:0 }}
      onDragOver={onDragOver}
      onDrop={e => onDrop(e, col.id)}
    >
      <div style={{
        background: col.bg, border:`1px solid ${col.color}33`,
        borderRadius:12, padding:"10px 12px", marginBottom:8,
        display:"flex", justifyContent:"space-between", alignItems:"center",
      }}>
        <div>
          <span style={{ fontSize:12, fontWeight:700, color:col.color }}>{col.label}</span>
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginLeft:8 }}>{leads.length}</span>
        </div>
        {total$ > 0 && <span style={{ fontSize:11, fontWeight:700, color:col.color }}>{fmt$(total$)}</span>}
      </div>
      <div style={{ minHeight:120 }}>
        {leads.map(lead => (
          <LeadCard
            key={lead.id}
            lead={lead}
            columnaColor={col.color}
            onDragStart={onDragStart}
            onTap={onTap}
            esAdmin={esAdmin}
            vendedores={vendedores}
          />
        ))}
      </div>
    </div>
  );
}

// ── LOGIN ────────────────────────────────────────────────────────
function Login({ vendedores, onLogin }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const admin = vendedores.find(x => x.rol === "admin") || vendedores[0];

  const entrar = () => {
    if (admin && pin === admin.pin) { onLogin(admin); }
    else { setError("PIN incorrecto"); setPin(""); }
  };

  return (
    <div style={{
      minHeight:"100vh", background:"#0F0F0F", display:"flex",
      alignItems:"center", justifyContent:"center", padding:24,
      fontFamily:"'DM Sans','Segoe UI',sans-serif",
    }}>
      <div style={{
        background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
        borderRadius:20, padding:36, width:"100%", maxWidth:380, textAlign:"center",
      }}>
        <div style={{
          width:52, height:52, borderRadius:14, margin:"0 auto 20px",
          background:"linear-gradient(135deg,#FF6B00,#CC4400)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontWeight:900, fontSize:20, color:"#fff",
        }}>QZ</div>
        <div style={{ fontSize:20, fontWeight:800, color:"#fff", marginBottom:4 }}>QZ SRL — CRM</div>
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.35)", marginBottom:28 }}>Ingresá con el PIN</div>

        <input
          type="password"
          value={pin}
          onChange={e => { setPin(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && entrar()}
          placeholder="PIN de 4 dígitos"
          maxLength={4}
          style={{
            width:"100%", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)",
            borderRadius:10, padding:"11px 14px", color:"#fff", fontSize:16,
            outline:"none", marginBottom:8, boxSizing:"border-box", textAlign:"center", letterSpacing:8,
          }}
        />

        {error && <div style={{ fontSize:12, color:"#EF4444", marginBottom:8 }}>{error}</div>}

        <button
          onClick={entrar}
          disabled={pin.length < 4}
          style={{
            width:"100%", padding:"12px 0", borderRadius:10, border:"none",
            background:"linear-gradient(135deg,#FF6B00,#CC4400)",
            color:"#fff", fontWeight:700, fontSize:14, cursor:"pointer",
            opacity: pin.length < 4 ? 0.4 : 1, marginTop:4,
          }}
        >Ingresar</button>
      </div>
    </div>
  );
}

// ── PANEL ADMIN — Agente ─────────────────────────────────────────
function PanelAgente({ regiones, onAgregarRegion }) {
  const [cfg, setCfg] = useState({ segmento:"construcción", ciudad:"Córdoba", radio:"40" });
  const [corriendo, setCorriendo] = useState(false);
  const [logs, setLogs] = useState([]);
  const [nuevaRegion, setNuevaRegion] = useState("");
  const logRef = useRef(null);

  const pasos = [
    `🔎 Iniciando agente — segmento: "${cfg.segmento}"`,
    `📍 Radio: ${cfg.radio} km desde ${cfg.ciudad}`,
    `📋 Páginas Amarillas → buscando...`,
    `   ✅ 6 empresas encontradas`,
    `📋 Guía Oleo → buscando...`,
    `   ✅ 4 empresas encontradas`,
    `📋 Cylex → buscando...`,
    `   ✅ 3 empresas encontradas`,
    `🔍 SerpAPI → buscando...`,
    `   ✅ 5 empresas encontradas`,
    `🤖 Groq → calificando 18 empresas únicas...`,
    `   ⭐ "Constructora Roca SRL" → score 89`,
    `   ⭐ "Metalúrgica Norte SA" → score 84`,
    `   ⚠️  "Talleres Express" → score 52 (descartado)`,
    `   ⭐ "Ingeniería Sur SRL" → score 77`,
    `💾 4 leads nuevos guardados`,
    `✅ Agente finalizado`,
  ];

  const ejecutar = () => {
    setCorriendo(true); setLogs([]);
    let i = 0;
    const iv = setInterval(() => {
      if (i < pasos.length) {
        setLogs(p => [...p, { t: new Date().toLocaleTimeString("es-AR"), m: pasos[i] }]);
        i++;
        setTimeout(() => { if (logRef.current) logRef.current.scrollTop = 9999; }, 30);
      } else {
        clearInterval(iv);
        setCorriendo(false);
      }
    }, 650);
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
      {/* Config agente */}
      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:16 }}>⚙️ Configurar búsqueda</div>
        {[
          { label:"Segmento / Rubro", key:"segmento", placeholder:"ej: construcción" },
          { label:"Ciudad base",      key:"ciudad",   placeholder:"ej: Córdoba" },
          { label:"Radio (km)",       key:"radio",    placeholder:"ej: 40" },
        ].map(f => (
          <div key={f.key} style={{ marginBottom:12 }}>
            <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:1, marginBottom:4 }}>{f.label}</div>
            <input
              value={cfg[f.key]}
              onChange={e => setCfg(p => ({...p,[f.key]:e.target.value}))}
              placeholder={f.placeholder}
              style={{
                width:"100%", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)",
                borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box",
              }}
            />
          </div>
        ))}
        <button
          onClick={ejecutar}
          disabled={corriendo}
          style={{
            width:"100%", padding:"11px 0", borderRadius:9, border:"none",
            background: corriendo ? "rgba(255,107,0,0.2)" : "linear-gradient(135deg,#FF6B00,#CC4400)",
            color:"#fff", fontWeight:700, fontSize:13, cursor: corriendo ? "not-allowed" : "pointer",
          }}
        >{corriendo ? "⏳ Ejecutando..." : "▶ Ejecutar Agente"}</button>

        {logs.length > 0 && (
          <div ref={logRef} style={{
            marginTop:14, background:"rgba(0,0,0,0.4)", borderRadius:8, padding:12,
            fontFamily:"monospace", fontSize:11, maxHeight:160, overflowY:"auto",
          }}>
            {logs.map((l,i) => (
              <div key={i} style={{ color:"rgba(255,255,255,0.65)", marginBottom:3, display:"flex", gap:8 }}>
                <span style={{ color:"rgba(255,255,255,0.2)", minWidth:64 }}>{l.t}</span>
                <span>{l.m}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Regiones */}
      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
        <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:16 }}>🗺️ Regiones activas</div>
        {regiones.map(r => (
          <div key={r} style={{
            display:"flex", alignItems:"center", gap:8, padding:"8px 12px",
            background:"rgba(255,107,0,0.08)", border:"1px solid rgba(255,107,0,0.2)",
            borderRadius:8, marginBottom:8,
          }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:"#FF6B00" }}/>
            <span style={{ fontSize:13, color:"rgba(255,255,255,0.8)" }}>{r}</span>
          </div>
        ))}
        <div style={{ display:"flex", gap:8, marginTop:12 }}>
          <input
            value={nuevaRegion}
            onChange={e => setNuevaRegion(e.target.value)}
            placeholder="Nueva región..."
            onKeyDown={e => {
              if (e.key==="Enter" && nuevaRegion.trim()) {
                onAgregarRegion(nuevaRegion.trim());
                setNuevaRegion("");
              }
            }}
            style={{
              flex:1, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.09)",
              borderRadius:8, padding:"8px 12px", color:"#fff", fontSize:13, outline:"none",
            }}
          />
          <button
            onClick={() => { if(nuevaRegion.trim()) { onAgregarRegion(nuevaRegion.trim()); setNuevaRegion(""); }}}
            style={{
              padding:"8px 14px", borderRadius:8, border:"none",
              background:"rgba(59,130,246,0.2)", color:"#FF6B00", cursor:"pointer", fontWeight:700,
            }}
          >+</button>
        </div>
      </div>
    </div>
  );
}

// ── TAB LEADS ────────────────────────────────────────────────────
function TabLeads({ leads }) {
  const [busqueda, setBusqueda]         = useState("");
  const [filtroEstado, setFiltroEstado]       = useState("All");
  const [filtroRegion, setFiltroRegion]       = useState("All");
  const [filtroEmpresaTab, setFiltroEmpresaTab] = useState("All");
  const [sortCol, setSortCol]           = useState("score");
  const [sortDir, setSortDir]           = useState("desc");
  const [importando, setImportando]     = useState(false);
  const [importMsg, setImportMsg]       = useState(null);

  const descargarTemplate = () => {
    const cols = ["empresa","contacto","email","fuente","segmento","ciudad"];
    const ejemplo = ["Acme S.L.","Juan García","j.garcia@acme.com","Base propia","Tecnología","Madrid"];
    const rows = [cols, ejemplo];
    const csv = rows.map(r => r.join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = "template_importar_leads.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importarXLS = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportando(true);
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const texto = ev.target.result;
        const lineas = texto.split("\n").map(l => l.trim()).filter(Boolean);
        if (lineas.length < 2) throw new Error("El archivo está vacío o solo tiene el header");
        const sep = lineas[0].includes(";") ? ";" : ",";
        const header = lineas[0].split(sep).map(h => h.replace(/"/g,"").trim().toLowerCase());
        const iEmpresa  = header.indexOf("empresa");
        const iContacto = header.indexOf("contacto");
        const iEmail    = header.indexOf("email");
        const iFuente   = header.indexOf("fuente");
        const iSegmento = header.indexOf("segmento");
        const iCiudad   = header.indexOf("ciudad");
        if (iEmpresa === -1 || iEmail === -1) {
          throw new Error("El archivo debe tener al menos las columnas 'empresa' y 'email'");
        }
        const parsearFila = (linea) => {
          const vals = [];
          let dentro = false, actual = "";
          for (const c of linea) {
            if (c === '"') { dentro = !dentro; }
            else if ((c === sep) && !dentro) { vals.push(actual.trim()); actual = ""; }
            else actual += c;
          }
          vals.push(actual.trim());
          return vals;
        };
        const leads_nuevos = lineas.slice(1).map((linea) => {
          const vals = parsearFila(linea);
          const empresa = vals[iEmpresa] || "";
          const email   = vals[iEmail]   || "";
          if (!empresa || !email) return null;
          return {
            empresa,
            contacto:  iContacto >= 0 ? vals[iContacto] || "" : "",
            email_estimado: email,
            fuente:    iFuente   >= 0 ? vals[iFuente]   || "Importación manual" : "Importación manual",
            segmento:  iSegmento >= 0 ? vals[iSegmento] || "" : "",
            ciudad:    iCiudad   >= 0 ? vals[iCiudad]   || "" : "",
            estado:    "Pendiente",
            score:     0,
            fecha:     new Date().toISOString().split("T")[0],
          };
        }).filter(Boolean);
        if (leads_nuevos.length === 0) {
          throw new Error("No se encontraron filas válidas (verificá que tengan empresa y email)");
        }
        fetch(`https://qzsrl.velqo.io/api/importar-leads`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ empresa: "qzsrl", leads: leads_nuevos }),
        })
        .then(r => r.json())
        .then(d => {
          setImportando(false);
          if (d.ok) {
            setImportMsg({ ok: true, texto: `${d.importados} leads importados correctamente. ${d.duplicados > 0 ? `(${d.duplicados} duplicados omitidos)` : ""}` });
          } else {
            setImportMsg({ ok: false, texto: `Error: ${d.error}` });
          }
        })
        .catch(() => {
          setImportando(false);
          setImportMsg({ ok: false, texto: "Error de conexión al importar" });
        });
      } catch (err) {
        setImportando(false);
        setImportMsg({ ok: false, texto: err.message });
      }
    };
    reader.readAsText(file, "utf-8");
    e.target.value = "";
  };

  // leadsFiltrados se declara abajo — exportCSV lo usa via closure
  const exportCSV = () => {
    const cols = ["ID","Empresa","Contacto","Cargo","Email","Telefono","Ciudad","Region","Segmento","Fuente","Estado","Score","Monto","Fecha","Notas","Empresa Captio"];
    const filtered = leads.filter(l => {
      if (filtroEstado !== "All" && l.estado !== filtroEstado) return false;
      if (filtroRegion !== "All" && l.region !== filtroRegion) return false;
      if (filtroEmpresaTab !== "All" && l.empresa_captio !== filtroEmpresaTab) return false;
      if (busqueda && !`${l.empresa}${l.contacto}${l.email}${l.segmento}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    });
    const rows = filtered.map(l => [
      l.id, l.empresa, l.contacto, l.cargo, l.email, l.tel,
      l.ciudad, l.region, l.segmento, l.fuente, l.estado,
      l.score, l.monto, l.fecha, l.notas, l.empresa_captio,
    ]);
    const csv = [cols, ...rows]
      .map(row => row.map(v => '"' + String(v||"").replace(/"/g, '""') + '"').join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type:"text/csv;charset=utf-8;" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `leads_captio_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const estados   = ["All", ...Array.from(new Set(leads.map(l=>l.estado).filter(Boolean)))];
  const regiones  = ["All", ...Array.from(new Set(leads.map(l=>l.region).filter(Boolean)))];
  const empresas  = ["All", "QZ SRL", "Quick Clean", "Air Quality"];

  const leadsFiltrados = leads
    .filter(l => {
      if (filtroEstado !== "All" && l.estado !== filtroEstado) return false;
      if (filtroRegion !== "All" && l.region !== filtroRegion) return false;
      if (filtroEmpresaTab !== "All" && l.empresa_captio !== filtroEmpresaTab) return false;
      if (busqueda && !`${l.empresa}${l.contacto}${l.email}${l.segmento}`.toLowerCase().includes(busqueda.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const va = a[sortCol] ?? "";
      const vb = b[sortCol] ?? "";
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });

  const toggleSort = col => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const COLS = [
    { key:"empresa",       label:"Empresa"   },
    { key:"contacto",      label:"Contacto"  },
    { key:"email",         label:"Email"     },
    { key:"empresa_captio",label:"Proyecto"  },
    { key:"region",        label:"Región"    },
    { key:"segmento",      label:"Segmento"  },
    { key:"fuente",        label:"Fuente"    },
    { key:"estado",        label:"Estado"    },
    { key:"score",         label:"Score"     },
    { key:"monto",         label:"Monto"     },
    { key:"fecha",         label:"Fecha"     },
  ];

  const estadoColor = {
    Nuevo:"#6B7280", Validado:"#3B82F6", Cotizado:"#F59E0B",
    Ganado:"#10B981", Perdido:"#EF4444"
  };
  const empColor = {
    "QZ SRL":"#FF6B00", "Quick Clean":"#F59E0B", "Air Quality":"#06B6D4"
  };

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:18, fontWeight:800, color:"#fff" }}>📋 Leads</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{leadsFiltrados.length} de {leads.length} leads</div>
        </div>
        <button onClick={descargarTemplate} style={{
          padding:"9px 16px", borderRadius:8,
          border:"1px solid rgba(255,255,255,0.15)", background:"transparent",
          color:"rgba(255,255,255,0.5)", fontWeight:600, fontSize:12, cursor:"pointer"
        }}>Template CSV</button>
        <label style={{
          padding:"9px 16px", borderRadius:8,
          border:"1px solid rgba(251,191,36,0.4)", background:"rgba(251,191,36,0.1)",
          color:"#FBBF24", fontWeight:700, fontSize:13, cursor:"pointer"
        }}>
          {importando ? "Importando..." : "Importar CSV"}
          <input type="file" accept=".csv,.xlsx,.xls" style={{ display:"none" }} onChange={importarXLS} disabled={importando} />
        </label>
        <button onClick={exportCSV} style={{
          padding:"9px 20px", borderRadius:8,
          border:"1px solid rgba(251,191,36,0.4)", background:"rgba(251,191,36,0.1)",
          color:"#FBBF24", fontWeight:700, fontSize:13, cursor:"pointer"
        }}>Exportar CSV</button>
      </div>

      {importMsg && (
        <div style={{
          marginBottom:14, padding:"10px 14px", borderRadius:8, fontSize:13,
          background: importMsg.ok ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
          border: `1px solid ${importMsg.ok ? "#10B981" : "#EF4444"}`,
          color: importMsg.ok ? "#10B981" : "#EF4444",
        }}>
          {importMsg.texto}
          <button onClick={() => setImportMsg(null)} style={{ float:"right", background:"none", border:"none", cursor:"pointer", color:"inherit", fontSize:14 }}>x</button>
        </div>
      )}

      <div style={{ display:"flex", gap:10, marginBottom:16, flexWrap:"wrap" }}>
        <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="🔍 Buscar empresa, contacto, email..."
          style={{
            flex:1, minWidth:200, background:"rgba(255,255,255,0.05)",
            border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
            padding:"8px 14px", color:"#fff", fontSize:13, outline:"none"
          }}/>
        <select value={filtroEmpresaTab} onChange={e => setFiltroEmpresaTab(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"8px 12px", color:"#fff", fontSize:13, outline:"none"
        }}>
          {empresas.map(e => <option key={e} value={e}>{e === "All" ? "Todas las empresas" : e}</option>)}
        </select>
        <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"8px 12px", color:"#fff", fontSize:13, outline:"none"
        }}>
          {estados.map(e => <option key={e} value={e}>{e === "All" ? "Todos los estados" : e}</option>)}
        </select>
        <select value={filtroRegion} onChange={e => setFiltroRegion(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"8px 12px", color:"#fff", fontSize:13, outline:"none"
        }}>
          {regiones.map(r => <option key={r} value={r}>{r === "All" ? "Todas las regiones" : r}</option>)}
        </select>
      </div>

      <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
            <thead>
              <tr>
                {COLS.map(col => (
                  <th key={col.key} onClick={() => toggleSort(col.key)} style={{
                    padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700,
                    color: sortCol === col.key ? "#FBBF24" : "rgba(255,255,255,0.4)",
                    textTransform:"uppercase", letterSpacing:"0.07em", cursor:"pointer",
                    borderBottom:`2px solid ${sortCol === col.key ? "#FBBF24" : "rgba(255,255,255,0.06)"}`,
                    whiteSpace:"nowrap", userSelect:"none",
                    background:"rgba(0,0,0,0.2)",
                  }}>
                    {col.label} {sortCol === col.key ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leadsFiltrados.length === 0 && (
                <tr><td colSpan={COLS.length} style={{ padding:40, textAlign:"center", color:"rgba(255,255,255,0.3)", fontSize:13 }}>Sin leads</td></tr>
              )}
              {leadsFiltrados.map((lead, i) => (
                <tr key={`${lead.id}-${i}`} style={{
                  borderTop:"1px solid rgba(255,255,255,0.04)",
                  background: i%2===0 ? "rgba(255,255,255,0.01)" : "transparent",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,107,0,0.05)"}
                onMouseLeave={e => e.currentTarget.style.background = i%2===0 ? "rgba(255,255,255,0.01)" : "transparent"}>
                  <td style={{ padding:"9px 12px", color:"#fff", fontWeight:600, maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{lead.empresa}</td>
                  <td style={{ padding:"9px 12px", color:"rgba(255,255,255,0.7)" }}>
                    <div style={{ fontWeight:500 }}>{lead.contacto}</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{lead.cargo}</div>
                  </td>
                  <td style={{ padding:"9px 12px", color:"rgba(255,255,255,0.5)", fontSize:11 }}>{lead.email}</td>
                  <td style={{ padding:"9px 12px" }}>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
                      background:`${empColor[lead.empresa_captio]||"#6B7280"}20`,
                      color: empColor[lead.empresa_captio]||"#6B7280", whiteSpace:"nowrap"
                    }}>{lead.empresa_captio}</span>
                  </td>
                  <td style={{ padding:"9px 12px" }}>
                    <span style={{ fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, background:"rgba(251,191,36,0.1)", color:"#FBBF24", whiteSpace:"nowrap" }}>{lead.region}</span>
                  </td>
                  <td style={{ padding:"9px 12px", color:"rgba(255,255,255,0.5)", fontSize:11 }}>{lead.segmento}</td>
                  <td style={{ padding:"9px 12px", color:"rgba(255,255,255,0.5)", fontSize:11 }}>{lead.fuente}</td>
                  <td style={{ padding:"9px 12px" }}>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10,
                      background:`${estadoColor[lead.estado]||"#6B7280"}20`,
                      color: estadoColor[lead.estado]||"#6B7280", whiteSpace:"nowrap"
                    }}>{lead.estado}</span>
                  </td>
                  <td style={{ padding:"9px 12px" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:36, height:4, background:"rgba(255,255,255,0.08)", borderRadius:2, overflow:"hidden" }}>
                        <div style={{ width:`${lead.score}%`, height:"100%", background:scoreColor(lead.score), borderRadius:2 }}/>
                      </div>
                      <span style={{ fontSize:10, fontWeight:700, color:scoreColor(lead.score) }}>{lead.score}</span>
                    </div>
                  </td>
                  <td style={{ padding:"9px 12px", color:"#FBBF24", fontWeight:700, fontSize:11, whiteSpace:"nowrap" }}>{lead.monto > 0 ? fmt$(lead.monto) : "—"}</td>
                  <td style={{ padding:"9px 12px", color:"rgba(255,255,255,0.35)", fontSize:10, whiteSpace:"nowrap" }}>{lead.fecha}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {leadsFiltrados.length > 0 && (
          <div style={{ padding:"12px 16px", borderTop:"1px solid rgba(255,255,255,0.05)", display:"flex", gap:24, flexWrap:"wrap" }}>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>
              <strong style={{ color:"#fff" }}>{leadsFiltrados.length}</strong> leads
            </span>
            <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>
              Score promedio: <strong style={{ color:"#fff" }}>{Math.round(leadsFiltrados.reduce((a,b)=>a+b.score,0)/leadsFiltrados.length)}</strong>
            </span>
            {leadsFiltrados.some(l=>l.monto>0) && (
              <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>
                Total: <strong style={{ color:"#FBBF24" }}>{fmt$(leadsFiltrados.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0))}</strong>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── TAB ACTIVIDAD ────────────────────────────────────────────────
function TabActividad({ leads, esAdmin }) {
  const [logs, setLogs]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [filtroEmp, setFiltroEmp]   = useState("todas");
  const [filtroTipo, setFiltroTipo] = useState("todos");

  const cargar = () => {
    setLoading(true);
    let url = "https://qzsrl.velqo.io/api/actividad?limit=100";
    if (filtroEmp !== "todas") url += `&empresa=${filtroEmp}`;
    fetch(url)
      .then(r => r.json())
      .then(data => { setLogs(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { cargar(); }, [filtroEmp]);

  const logsFiltrados = filtroTipo === "todos" ? logs : logs.filter(l => l.tipo === filtroTipo);

  const tipoLabel = { presentacion:"Presentación", "5preguntas":"5 Preguntas", presupuesto:"Presupuesto", minerva_ok:"Minerva ✓", minerva_vendedor:"Minerva → Vendedor" };
  const tipoColor = { presentacion:"#3B82F6", "5preguntas":"#8B5CF6", presupuesto:"#F59E0B", minerva_ok:"#10B981", minerva_vendedor:"#FF6B00" };
  const empColor  = { qzsrl:"#FF6B00", lowcost:"#F59E0B", airquality:"#06B6D4" };
  const empLabel  = { qzsrl:"QZ SRL", lowcost:"Quick Clean", airquality:"Air Quality" };

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <div style={{ fontSize:15, fontWeight:800, color:"#fff", flex:1 }}>⚡ Actividad del sistema</div>
        <select value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"7px 12px", color:"#fff", fontSize:12, outline:"none",
        }}>
          <option value="todas">Todas las empresas</option>
          <option value="qzsrl">QZ SRL</option>
          <option value="lowcost">Quick Clean</option>
          <option value="airquality">Air Quality</option>
        </select>
        <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={{
          background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:8, padding:"7px 12px", color:"#fff", fontSize:12, outline:"none",
        }}>
          <option value="todos">Todos los tipos</option>
          <option value="presentacion">Presentación</option>
          <option value="5preguntas">5 Preguntas</option>
          <option value="presupuesto">Presupuesto</option>
          <option value="minerva_ok">Minerva ✓</option>
          <option value="minerva_vendedor">Minerva → Vendedor</option>
        </select>
        <button onClick={cargar} style={{
          padding:"7px 14px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
          background:"transparent", color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:12,
        }}>↻ Actualizar</button>
      </div>

      {loading && <div style={{ fontSize:13, color:"rgba(255,255,255,0.3)", textAlign:"center", padding:40 }}>Cargando...</div>}
      {!loading && logsFiltrados.length === 0 && (
        <div style={{ fontSize:13, color:"rgba(255,255,255,0.2)", textAlign:"center", padding:60 }}>
          Sin actividad registrada aún
        </div>
      )}
      {!loading && logsFiltrados.length > 0 && (
        <div style={{ background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
          {logsFiltrados.map((log, i) => {
            const lead = leads.find(l => String(l.id) === String(log.lead_id));
            const fecha = new Date(log.creado_en).toLocaleString("es-AR", {
              day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit"
            });
            return (
              <div key={log.id} style={{
                display:"flex", alignItems:"center", gap:12, padding:"11px 16px",
                borderBottom: i < logsFiltrados.length-1 ? "1px solid rgba(255,255,255,0.05)" : "none",
              }}>
                <span style={{ fontSize:14, minWidth:16 }}>{log.estado === "enviado" ? "✅" : "❌"}</span>
                <span style={{
                  fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, whiteSpace:"nowrap",
                  background:`${tipoColor[log.tipo]||"#6B7280"}20`,
                  color: tipoColor[log.tipo]||"#6B7280",
                }}>{tipoLabel[log.tipo]||log.tipo}</span>
                <span style={{
                  fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:10, whiteSpace:"nowrap",
                  background:`${empColor[log.empresa]||"#6B7280"}20`,
                  color: empColor[log.empresa]||"#6B7280",
                }}>{empLabel[log.empresa]||log.empresa}</span>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:"#fff", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {lead?.empresa || `Lead #${log.lead_id}`}
                  </div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {log.destinatario}
                  </div>
                </div>
                {log.enviado_por && (
                  <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)", whiteSpace:"nowrap" }}>
                    👤 {log.enviado_por}
                  </span>
                )}
                <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)", whiteSpace:"nowrap" }}>{fecha}</span>
              </div>
            );
          })}
        </div>
      )}

      {!loading && logs.length > 0 && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:10, marginTop:16 }}>
          {[
            { label:"Total enviados",  value: logs.filter(l=>l.estado==="enviado").length, color:"#10B981" },
            { label:"Presentaciones",  value: logs.filter(l=>l.tipo==="presentacion").length, color:"#3B82F6" },
            { label:"5 Preguntas",     value: logs.filter(l=>l.tipo==="5preguntas").length, color:"#8B5CF6" },
            { label:"Presupuestos",    value: logs.filter(l=>l.tipo==="presupuesto").length, color:"#F59E0B" },
            { label:"Errores",         value: logs.filter(l=>l.estado==="error").length, color:"#EF4444" },
          ].map(k => (
            <div key={k.label} style={{
              background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
              borderRadius:10, padding:"12px 14px",
            }}>
              <div style={{ fontSize:22, fontWeight:900, color:k.color }}>{k.value}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginTop:4 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── EMAIL SECTION (sub-tabs) ─────────────────────────────────────
// ── TAB SALUD DE EMAIL ────────────────────────────────────────────
function TabEmailHealth() {
  const [health, setHealth] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEmp, setSelectedEmp] = useState("all");

  const apiBase = "https://qzsrl.velqo.io";
  const empresas = ["qzsrl", "lowcost", "airquality"];
  const empLabel = { qzsrl:"QZ SRL", lowcost:"Quick Clean", airquality:"Air Quality" };
  const empColor = { qzsrl:"#FF6B00", lowcost:"#F59E0B", airquality:"#06B6D4" };

  const fetchHealth = () => {
    setLoading(true);
    Promise.all(empresas.map(emp =>
      fetch(`${apiBase}/api/email-health/${emp}`)
        .then(r => r.ok ? r.json() : null)
        .catch(() => null)
    )).then(results => {
      const h = {};
      empresas.forEach((emp, i) => { if (results[i]) h[emp] = results[i]; });
      setHealth(h);
      setLoading(false);
    });
  };

  useEffect(() => { fetchHealth(); }, []);

  const getScoreColor = (score) => {
    if (score >= 80) return "#10B981";
    if (score >= 60) return "#F59E0B";
    if (score >= 40) return "#F97316";
    return "#EF4444";
  };

  const getEnfriamientoLabel = (e) => {
    if (e === "critico") return { label:"BLOQUEADO", color:"#EF4444", bg:"rgba(239,68,68,0.1)" };
    if (e === "frio")    return { label:"ENFRIADO", color:"#F97316", bg:"rgba(249,115,22,0.1)" };
    if (e === "tibio")   return { label:"REDUCIDO", color:"#F59E0B", bg:"rgba(245,158,11,0.1)" };
    return { label:"NORMAL", color:"#10B981", bg:"rgba(16,185,129,0.1)" };
  };

  if (loading) return <div style={{ textAlign:"center", padding:60, color:"rgba(255,255,255,0.3)", fontSize:13 }}>Verificando salud de emails...</div>;

  const empsConDatos = Object.keys(health);
  if (!empsConDatos.length) return (
    <div style={{ textAlign:"center", padding:60, color:"rgba(255,255,255,0.3)" }}>
      <div style={{ fontSize:40, marginBottom:12 }}>📧</div>
      <div style={{ fontSize:14 }}>No hay SMTP configurado en ninguna empresa</div>
      <div style={{ fontSize:12, marginTop:4 }}>Configurá el correo en Admin → SMTP para ver la salud</div>
    </div>
  );

  const visibles = selectedEmp === "all" ? empsConDatos : empsConDatos.filter(e => e === selectedEmp);

  return (
    <div>
      {/* Filtro empresa */}
      {empsConDatos.length > 1 && (
        <div style={{ display:"flex", gap:4, marginBottom:16 }}>
          <button onClick={() => setSelectedEmp("all")} style={{
            padding:"5px 14px", borderRadius:7, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
            background: selectedEmp==="all" ? "rgba(255,255,255,0.1)" : "transparent",
            color: selectedEmp==="all" ? "#fff" : "rgba(255,255,255,0.4)",
          }}>Todas</button>
          {empsConDatos.map(emp => (
            <button key={emp} onClick={() => setSelectedEmp(emp)} style={{
              padding:"5px 14px", borderRadius:7, border:"none", cursor:"pointer", fontSize:11, fontWeight:600,
              background: selectedEmp===emp ? `${empColor[emp]}22` : "transparent",
              color: selectedEmp===emp ? empColor[emp] : "rgba(255,255,255,0.4)",
            }}>{empLabel[emp]}</button>
          ))}
          <button onClick={fetchHealth} style={{
            marginLeft:"auto", padding:"5px 12px", borderRadius:7, border:"1px solid rgba(255,255,255,0.08)",
            background:"transparent", color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:11,
          }}>↻ Actualizar</button>
        </div>
      )}

      {visibles.map(emp => {
        const h = health[emp];
        const sc = h.score;
        const scColor = getScoreColor(sc);
        const enf = getEnfriamientoLabel(h.enfriamiento);

        return (
          <div key={emp} style={{ marginBottom:24, background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, overflow:"hidden" }}>
            {/* Header empresa */}
            <div style={{ padding:"16px 20px", borderBottom:"1px solid rgba(255,255,255,0.06)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ width:8, height:8, borderRadius:"50%", background:empColor[emp] }}/>
                <span style={{ fontWeight:700, fontSize:14 }}>{empLabel[emp]}</span>
                <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{h.checks.email}</span>
              </div>
              <span style={{ padding:"3px 10px", borderRadius:20, fontSize:11, fontWeight:700, background:enf.bg, color:enf.color }}>
                {enf.label} — máx {h.maxDiario}/día
              </span>
            </div>

            <div style={{ padding:20 }}>
              {/* Termómetro + checks */}
              <div style={{ display:"grid", gridTemplateColumns:"200px 1fr", gap:24 }}>
                {/* Termómetro */}
                <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                  <svg width="160" height="160" viewBox="0 0 160 160">
                    <circle cx="80" cy="80" r="68" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="12"/>
                    <circle cx="80" cy="80" r="68" fill="none" stroke={scColor} strokeWidth="12"
                      strokeDasharray={`${(sc / 100) * 427} 427`}
                      strokeLinecap="round"
                      transform="rotate(-90 80 80)"
                      style={{ transition:"stroke-dasharray 0.8s ease" }}
                    />
                    <text x="80" y="72" textAnchor="middle" fill={scColor} fontSize="36" fontWeight="800">{sc}</text>
                    <text x="80" y="92" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="12">/ 100</text>
                  </svg>
                  <div style={{ marginTop:8, fontSize:12, color:scColor, fontWeight:700 }}>
                    {sc >= 80 ? "Excelente" : sc >= 60 ? "Aceptable" : sc >= 40 ? "Mejorable" : "Crítico"}
                  </div>
                </div>

                {/* Checks detalle */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {[
                    { label:"SPF", data:h.checks.spf, desc:"Autoriza servidores de envío" },
                    { label:"DKIM", data:h.checks.dkim, desc:"Firma digital del email" },
                    { label:"DMARC", data:h.checks.dmarc, desc:"Política de autenticación" },
                    { label:"MX", data:h.checks.mx, desc:"Registros de correo" },
                  ].map(c => (
                    <div key={c.label} style={{ padding:"12px 14px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8 }}>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontWeight:700, fontSize:12 }}>{c.label}</span>
                        <span style={{
                          fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:12,
                          background: c.data?.ok ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                          color: c.data?.ok ? "#10B981" : "#EF4444",
                        }}>{c.data?.ok ? "OK" : "FALTA"}</span>
                      </div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>{c.desc}</div>
                      {c.data?.warning && <div style={{ fontSize:10, color:"#F59E0B", marginTop:4 }}>⚠ {c.data.warning}</div>}
                      {c.data?.ok && c.data?.record && (
                        <div style={{ fontSize:9, color:"rgba(255,255,255,0.25)", marginTop:4, wordBreak:"break-all", maxHeight:30, overflow:"hidden" }}>
                          {typeof c.data.record === "string" ? c.data.record.substring(0, 80) : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Blacklists */}
              {h.checks.ip && (
                <div style={{ marginTop:16, padding:"12px 14px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8 }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
                    <span style={{ fontWeight:700, fontSize:12 }}>Listas negras — IP {h.checks.ip}</span>
                    <span style={{
                      fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:12,
                      background: h.checks.blacklists.length === 0 ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
                      color: h.checks.blacklists.length === 0 ? "#10B981" : "#EF4444",
                    }}>
                      {h.checks.blacklists.length === 0 ? "LIMPIO" : `${h.checks.blacklists.length} LISTA(S)`}
                    </span>
                  </div>
                  {h.checks.blacklists.length > 0 && (
                    <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                      {h.checks.blacklists.map(bl => (
                        <span key={bl.list} style={{ fontSize:10, padding:"2px 8px", borderRadius:12, background:"rgba(239,68,68,0.08)", color:"#EF4444" }}>
                          {bl.list}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Métricas internas */}
              {h.metricas.totalHistorico > 0 && (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginTop:16 }}>
                  {[
                    { label:"Enviados", value:h.metricas.totalHistorico, color:"#3B82F6" },
                    { label:"Abiertos", value:h.metricas.abiertosHistorico, color:"#10B981" },
                    { label:"Errores", value:h.metricas.erroresHistorico, color:"#EF4444" },
                    { label:"Tasa apertura", value: h.metricas.tasaApertura !== null ? h.metricas.tasaApertura + "%" : "—", color:"#F59E0B" },
                  ].map(m => (
                    <div key={m.label} style={{ textAlign:"center", padding:"10px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:8 }}>
                      <div style={{ fontSize:22, fontWeight:800, color:m.color }}>{m.value}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{m.label}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Recomendaciones */}
              {h.recomendaciones.length > 0 && (
                <div style={{ marginTop:16 }}>
                  <div style={{ fontWeight:700, fontSize:12, marginBottom:8, color:"rgba(255,255,255,0.6)" }}>Recomendaciones</div>
                  {h.recomendaciones.map((rec, i) => (
                    <div key={i} style={{ display:"flex", gap:8, alignItems:"flex-start", padding:"8px 0", borderBottom: i < h.recomendaciones.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none" }}>
                      <span style={{ color:"#F59E0B", fontSize:12, flexShrink:0 }}>⚠</span>
                      <span style={{ fontSize:11, color:"rgba(255,255,255,0.5)", lineHeight:"1.4" }}>{rec}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Timestamp */}
              <div style={{ marginTop:12, fontSize:10, color:"rgba(255,255,255,0.2)", textAlign:"right" }}>
                Verificado: {new Date(h.checkedAt).toLocaleString("es-AR")}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmailSection({ esAdmin, schedule, setSchedule, leads }) {
  const [subTab, setSubTab] = useState("seguimiento");

  const subtabs = [
    { id: "seguimiento", label: "📊 Seguimiento" },
    { id: "plantillas",  label: "📝 Plantillas" },
    { id: "salud",       label: "🌡️ Salud Email" },
  ];

  return (
    <div>
      {/* Toggle envío automático */}
      {esAdmin && (
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16, padding:"12px 16px", background:"rgba(255,255,255,0.02)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10 }}>
          <div>
            <div style={{ fontWeight:700, fontSize:13, color:"#fff" }}>Envío automático de emails</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:2 }}>
              {schedule.envio_automatico
                ? "El sistema envía presentaciones y seguimientos automáticamente"
                : "Los emails solo se envían de forma manual desde la ficha del lead"}
            </div>
          </div>
          <button onClick={() => {
            const nuevo = !schedule.envio_automatico;
            if (!window.confirm(nuevo
              ? "¿Activar el envío automático de emails?"
              : "¿Desactivar el envío automático?"
            )) return;
            const nuevoSchedule = { ...schedule, envio_automatico: nuevo };
            setSchedule(nuevoSchedule);
            fetch(`https://qzsrl.velqo.io/api/schedule/qzsrl`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(nuevoSchedule),
            }).catch(() => {});
          }} style={{
            padding:"6px 16px", borderRadius:20, border:"none", cursor:"pointer", fontSize:12, fontWeight:600,
            background: schedule.envio_automatico ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
            color: schedule.envio_automatico ? "#10B981" : "#EF4444",
          }}>
            {schedule.envio_automatico ? "● Activado" : "○ Desactivado"}
          </button>
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"1px solid rgba(255,255,255,0.08)", paddingBottom:0 }}>
        {subtabs.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} style={{
            padding:"10px 20px", fontSize:13, fontWeight: subTab === st.id ? 700 : 500, cursor:"pointer",
            background:"none", border:"none", borderBottom: subTab === st.id ? "2px solid #fff" : "2px solid transparent",
            color: subTab === st.id ? "#fff" : "rgba(255,255,255,0.4)",
            marginBottom:-1,
          }}>{st.label}</button>
        ))}
      </div>

      {subTab === "seguimiento" && <TabSeguimiento leads={leads} />}
      {subTab === "plantillas" && <TabEmailEditor />}
      {subTab === "salud" && <TabEmailHealth />}
    </div>
  );
}

// ── TAB SEGUIMIENTO DE EMAILS ────────────────────────────────────
function TabSeguimiento({ leads }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [filtroEmp, setFiltroEmp] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [hiloAbierto, setHiloAbierto] = useState(null);

  const apiBase = "https://qzsrl.velqo.io";
  const empresas = ["qzsrl", "lowcost", "airquality"];
  const empLabel = { qzsrl:"QZ SRL", lowcost:"Quick Clean", airquality:"Air Quality" };
  const empColor = { qzsrl:"#FF6B00", lowcost:"#F59E0B", airquality:"#06B6D4" };

  useEffect(() => {
    setLoading(true);
    Promise.all(empresas.map(emp =>
      fetch(`${apiBase}/api/seguimiento/${emp}`).then(r => r.json()).then(data => (data || []).map(d => ({ ...d, emp })))
    ))
      .then(results => { setLogs(results.flat()); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Agrupar logs por lead_id
  const porLead = {};
  logs.forEach(log => {
    const key = log.lead_id;
    if (!porLead[key]) porLead[key] = { lead_id: key, email: log.destinatario, emp: log.emp, envios: [], estados: new Set() };
    porLead[key].envios.push(log);
    porLead[key].estados.add(log.estado);
    if (log.abierto_en) porLead[key].abierto = true;
    if (log.veces_abierto > 0) porLead[key].vecesAbierto = (porLead[key].vecesAbierto || 0) + log.veces_abierto;
  });

  const leadsAgrupados = Object.values(porLead).map(g => {
    const lead = leads.find(l => String(l.id) === String(g.lead_id));
    const ultimo = g.envios[0];
    let estadoFinal = "enviado";
    if (g.estados.has("rebote")) estadoFinal = "rebote";
    else if (g.estados.has("baja")) estadoFinal = "baja";
    else if (lead?.etiqueta_respuesta) estadoFinal = "respondido";
    else if (lead?.estado === "Contestado" || lead?.estado === "Validado") estadoFinal = "respondido";
    else if (g.abierto) estadoFinal = "abierto";
    return { ...g, lead, ultimo, estadoFinal };
  });

  // Métricas
  const metricLeads = filtroEmp === "todos" ? leadsAgrupados : leadsAgrupados.filter(l => l.emp === filtroEmp);
  const total = metricLeads.length;
  const abiertos = metricLeads.filter(l => l.abierto).length;
  const respondidos = metricLeads.filter(l => l.estadoFinal === "respondido").length;
  const bajas = metricLeads.filter(l => l.estadoFinal === "baja").length;
  const rebotes = metricLeads.filter(l => l.estadoFinal === "rebote").length;
  const pctAbiertos = total ? Math.round(abiertos / total * 100) : 0;
  const pctRespondidos = total ? Math.round(respondidos / total * 100) : 0;

  // Filtrar
  let filtrados = leadsAgrupados;
  if (filtroEmp !== "todos") filtrados = filtrados.filter(l => l.emp === filtroEmp);
  if (filtro === "abiertos") filtrados = filtrados.filter(l => l.abierto && l.estadoFinal !== "respondido");
  else if (filtro === "sin_abrir") filtrados = filtrados.filter(l => !l.abierto && l.estadoFinal === "enviado");
  else if (filtro === "respondidos") filtrados = filtrados.filter(l => l.estadoFinal === "respondido");
  else if (filtro === "bajas") filtrados = filtrados.filter(l => l.estadoFinal === "baja");
  else if (filtro === "rebotes") filtrados = filtrados.filter(l => l.estadoFinal === "rebote");

  if (busqueda) {
    const q = busqueda.toLowerCase();
    filtrados = filtrados.filter(l =>
      (l.lead?.empresa || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q)
    );
  }

  const estadoBadge = (est) => {
    const map = {
      enviado:    { bg: "rgba(59,130,246,0.15)", color: "#60A5FA", label: "enviado" },
      abierto:    { bg: "rgba(16,185,129,0.15)", color: "#34D399", label: "abierto" },
      respondido: { bg: "rgba(99,102,241,0.15)", color: "#818CF8", label: "respondido" },
      baja:       { bg: "rgba(239,68,68,0.15)",  color: "#F87171", label: "baja" },
      rebote:     { bg: "rgba(239,68,68,0.15)",  color: "#F87171", label: "rebote" },
    };
    const s = map[est] || map.enviado;
    return { padding:"3px 10px", borderRadius:20, fontSize:10, fontWeight:700, background:s.bg, color:s.color, display:"inline-block" };
  };

  const formatFecha = (f) => {
    if (!f) return { fecha: "", rel: "" };
    const d = new Date(f);
    const ahora = new Date();
    const diff = Math.floor((ahora - d) / 1000);
    const rel = diff < 3600 ? `hace ${Math.floor(diff/60)}m` : diff < 86400 ? `hace ${Math.floor(diff/3600)}h` : `hace ${Math.floor(diff/86400)}d`;
    return { fecha: d.toLocaleDateString("es-ES", { day:"2-digit", month:"short" }) + ", " + d.toLocaleTimeString("es-ES", { hour:"2-digit", minute:"2-digit" }), rel };
  };

  const tipoLabel = { presentacion:"Presentación", "5preguntas":"5 Preguntas", reintento1:"Reintento 1", reintento2:"Reintento 2", reintento3:"Reintento 3", gracias_presupuesto:"Gracias (presupuesto)", gracias_contacto:"Gracias (contacto)", gracias_otro:"Gracias (otro)" };

  if (loading) return <div style={{ padding:60, textAlign:"center", color:"rgba(255,255,255,0.3)", fontSize:13 }}>Cargando seguimiento...</div>;

  // Vista de hilo
  if (hiloAbierto) {
    const grupo = leadsAgrupados.find(l => l.lead_id === hiloAbierto);
    if (!grupo) { setHiloAbierto(null); return null; }
    return (
      <div>
        <button onClick={() => setHiloAbierto(null)} style={{ background:"none", border:"none", cursor:"pointer", fontSize:13, color:"#60A5FA", fontWeight:600, marginBottom:16, padding:0 }}>
          ← Volver al seguimiento
        </button>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:10, background:`${empColor[grupo.emp] || "#666"}30`, color: empColor[grupo.emp] || "#666" }}>
            {empLabel[grupo.emp] || grupo.emp}
          </span>
          <div>
            <div style={{ fontWeight:800, fontSize:18, color:"#fff" }}>{grupo.lead?.empresa || `Lead #${grupo.lead_id}`}</div>
            <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)" }}>{grupo.email} {grupo.lead?.ciudad ? `· ${grupo.lead.ciudad}` : ""}</div>
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {[...grupo.envios].reverse().map((log, i) => {
            const f = formatFecha(log.creado_en);
            const abierto = log.abierto_en ? formatFecha(log.abierto_en) : null;
            return (
              <div key={i} style={{ padding:"14px 18px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12 }}>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    <span style={{ fontSize:14 }}>{log.estado === "enviado" ? "✅" : log.estado === "rebote" ? "📛" : "❌"}</span>
                    <span style={{ fontSize:12, fontWeight:700, color:"#fff" }}>{tipoLabel[log.tipo] || log.tipo}</span>
                    <span style={estadoBadge(log.estado)}>{log.estado}</span>
                  </div>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>{f.fecha}</span>
                </div>
                {log.asunto && <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)", marginBottom:4 }}>Asunto: {log.asunto}</div>}
                {abierto && <div style={{ fontSize:11, color:"#34D399", display:"flex", alignItems:"center", gap:6 }}>👁 Abierto: {abierto.fecha} ({abierto.rel}){log.veces_abierto > 1 && <span style={{ fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:10, background:"rgba(52,211,153,0.2)", color:"#34D399" }}>x{log.veces_abierto}</span>}</div>}
                {log.error_detalle && <div style={{ fontSize:11, color:"#F87171", marginTop:4 }}>Error: {log.error_detalle}</div>}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Métricas */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:12, marginBottom:20 }}>
        {[
          { label:"Contactados", value:total, color:"#fff", bar:"#fff" },
          { label:"Abiertos", value:abiertos, pct:pctAbiertos, color:"#34D399", bar:"#10B981" },
          { label:"Respondidos", value:respondidos, pct:pctRespondidos, color:"#818CF8", bar:"#6366F1" },
          { label:"Bajas", value:bajas, color:"#F87171", bar:"#EF4444" },
          { label:"Rebotes", value:rebotes, color:"#F87171", bar:"#EF4444" },
        ].map((m, i) => (
          <div key={i} style={{ padding:"16px 18px", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:12 }}>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:6 }}>{m.label}</div>
            <div style={{ display:"flex", alignItems:"baseline", gap:6 }}>
              <span style={{ fontSize:28, fontWeight:800, color:"#fff" }}>{m.value}</span>
              {m.pct !== undefined && <span style={{ fontSize:12, color:m.color, fontWeight:600 }}>{m.pct}%</span>}
            </div>
            <div style={{ height:3, background:"rgba(255,255,255,0.06)", borderRadius:2, marginTop:8 }}>
              <div style={{ height:3, borderRadius:2, background:m.bar, width: total ? `${(m.value/total)*100}%` : "0%" }} />
            </div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        {[
          { id:"todos", label:"Todos" },
          { id:"abiertos", label:"👁 Abiertos" },
          { id:"sin_abrir", label:"Sin abrir" },
          { id:"respondidos", label:"💬 Respondidos" },
          { id:"bajas", label:"🚫 Bajas" },
          { id:"rebotes", label:"📛 Rebotes" },
        ].map(f => (
          <button key={f.id} onClick={() => setFiltro(f.id)} style={{
            padding:"6px 14px", borderRadius:20, border: filtro === f.id ? "1px solid #fff" : "1px solid rgba(255,255,255,0.1)",
            background: filtro === f.id ? "#fff" : "transparent",
            color: filtro === f.id ? "#0f172a" : "rgba(255,255,255,0.5)",
            fontSize:12, fontWeight:600, cursor:"pointer",
          }}>{f.label}</button>
        ))}
        <select value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} style={{
          padding:"6px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)",
          background:"transparent", color:"rgba(255,255,255,0.6)", fontSize:12, outline:"none",
        }}>
          <option value="todos">Todas las empresas</option>
          <option value="qzsrl">QZ SRL</option>
          <option value="lowcost">Quick Clean</option>
          <option value="airquality">Air Quality</option>
        </select>
        <input
          value={busqueda} onChange={e => setBusqueda(e.target.value)}
          placeholder="Buscar empresa / email..."
          style={{ marginLeft:"auto", padding:"7px 14px", borderRadius:8, border:"1px solid rgba(255,255,255,0.08)", background:"transparent", fontSize:12, color:"#fff", outline:"none", minWidth:200 }}
        />
      </div>

      {/* Tabla */}
      {filtrados.length === 0 && (
        <div style={{ textAlign:"center", padding:60, color:"rgba(255,255,255,0.2)", fontSize:13 }}>Sin resultados</div>
      )}
      {filtrados.length > 0 && (
        <div style={{ border:"1px solid rgba(255,255,255,0.06)", borderRadius:14, overflow:"hidden" }}>
          {/* Header */}
          <div style={{ display:"grid", gridTemplateColumns:"80px 2fr 1fr 1fr 80px 60px", padding:"10px 16px", background:"rgba(255,255,255,0.02)", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Empresa</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Lead</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Enviado</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Estado</span>
            <span style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"center" }}>Envíos</span>
            <span />
          </div>
          {filtrados.map((g, i) => {
            const f = formatFecha(g.ultimo?.creado_en);
            return (
              <div key={g.lead_id} style={{
                display:"grid", gridTemplateColumns:"80px 2fr 1fr 1fr 80px 60px", padding:"12px 16px", alignItems:"center",
                borderBottom: i < filtrados.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                cursor:"pointer",
              }} onClick={() => setHiloAbierto(g.lead_id)}>
                <span style={{ fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:10, background:`${empColor[g.emp] || "#666"}30`, color: empColor[g.emp] || "#666", whiteSpace:"nowrap", width:"fit-content" }}>
                  {empLabel[g.emp] || g.emp}
                </span>
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{g.lead?.empresa || `Lead #${g.lead_id}`}</div>
                  <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>{g.email} {g.lead?.ciudad ? `· ${g.lead.ciudad}` : ""}</div>
                </div>
                <div>
                  <div style={{ fontSize:12, color:"#fff" }}>{f.fecha}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{f.rel}</div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:4 }}><span style={estadoBadge(g.estadoFinal)}>{g.estadoFinal}</span>{g.vecesAbierto > 1 && <span style={{ fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:10, background:"rgba(52,211,153,0.2)", color:"#34D399" }}>👁 x{g.vecesAbierto}</span>}</div>
                <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fff" }}>{g.envios.length}</div>
                <div style={{ textAlign:"right", fontSize:12, color:"#60A5FA", fontWeight:600 }}>Ver hilo →</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── TEMPLATES DEFAULT QZ ────────────────────────────────────────
const TEMPLATES_DEFAULT_QZ = [
  { id: "presentacion_qz",  nombre: "1. Presentación",          tipo: "presentacion",        empresa: "qzsrl",      asunto: "", html: "" },
  { id: "reintento1_qz",    nombre: "2. Reintento día 30",      tipo: "reintento1",          empresa: "qzsrl",      asunto: "", html: "" },
  { id: "reintento2_qz",    nombre: "3. Reintento día 60",      tipo: "reintento2",          empresa: "qzsrl",      asunto: "", html: "" },
  { id: "reintento3_qz",    nombre: "4. Reintento día 90",      tipo: "reintento3",          empresa: "qzsrl",      asunto: "", html: "" },
  { id: "gracias_pres_qz",  nombre: "5. Gracias (presupuesto)", tipo: "gracias_presupuesto", empresa: "qzsrl",      asunto: "", html: "" },
  { id: "gracias_cont_qz",  nombre: "6. Gracias (contacto)",    tipo: "gracias_contacto",    empresa: "qzsrl",      asunto: "", html: "" },
  { id: "gracias_otro_qz",  nombre: "7. Gracias (otro)",        tipo: "gracias_otro",        empresa: "qzsrl",      asunto: "", html: "" },
  { id: "5preguntas_qz",    nombre: "8. 5 Preguntas",           tipo: "5preguntas",          empresa: "qzsrl",      asunto: "", html: "" },
  { id: "presentacion_lc",  nombre: "1. Presentación",          tipo: "presentacion",        empresa: "lowcost",    asunto: "", html: "" },
  { id: "reintento1_lc",    nombre: "2. Reintento día 30",      tipo: "reintento1",          empresa: "lowcost",    asunto: "", html: "" },
  { id: "reintento2_lc",    nombre: "3. Reintento día 60",      tipo: "reintento2",          empresa: "lowcost",    asunto: "", html: "" },
  { id: "reintento3_lc",    nombre: "4. Reintento día 90",      tipo: "reintento3",          empresa: "lowcost",    asunto: "", html: "" },
  { id: "gracias_pres_lc",  nombre: "5. Gracias (presupuesto)", tipo: "gracias_presupuesto", empresa: "lowcost",    asunto: "", html: "" },
  { id: "gracias_cont_lc",  nombre: "6. Gracias (contacto)",    tipo: "gracias_contacto",    empresa: "lowcost",    asunto: "", html: "" },
  { id: "gracias_otro_lc",  nombre: "7. Gracias (otro)",        tipo: "gracias_otro",        empresa: "lowcost",    asunto: "", html: "" },
  { id: "5preguntas_lc",    nombre: "8. 5 Preguntas",           tipo: "5preguntas",          empresa: "lowcost",    asunto: "", html: "" },
  { id: "presentacion_aq",  nombre: "1. Presentación",          tipo: "presentacion",        empresa: "airquality", asunto: "", html: "" },
  { id: "reintento1_aq",    nombre: "2. Reintento día 30",      tipo: "reintento1",          empresa: "airquality", asunto: "", html: "" },
  { id: "reintento2_aq",    nombre: "3. Reintento día 60",      tipo: "reintento2",          empresa: "airquality", asunto: "", html: "" },
  { id: "reintento3_aq",    nombre: "4. Reintento día 90",      tipo: "reintento3",          empresa: "airquality", asunto: "", html: "" },
  { id: "gracias_pres_aq",  nombre: "5. Gracias (presupuesto)", tipo: "gracias_presupuesto", empresa: "airquality", asunto: "", html: "" },
  { id: "gracias_cont_aq",  nombre: "6. Gracias (contacto)",    tipo: "gracias_contacto",    empresa: "airquality", asunto: "", html: "" },
  { id: "gracias_otro_aq",  nombre: "7. Gracias (otro)",        tipo: "gracias_otro",        empresa: "airquality", asunto: "", html: "" },
  { id: "5preguntas_aq",    nombre: "8. 5 Preguntas",           tipo: "5preguntas",          empresa: "airquality", asunto: "", html: "" },
];

const TIPOS_EMAIL_QZ = [
  { id: "presentacion",          label: "1. Presentación" },
  { id: "reintento1",            label: "2. Reintento día 30" },
  { id: "reintento2",            label: "3. Reintento día 60" },
  { id: "reintento3",            label: "4. Reintento día 90" },
  { id: "gracias_presupuesto",   label: "5. Gracias (presupuesto)" },
  { id: "gracias_contacto",      label: "6. Gracias (contacto)" },
  { id: "gracias_otro",          label: "7. Gracias (otro)" },
  { id: "5preguntas",            label: "8. 5 Preguntas" },
  { id: "custom",                label: "Personalizado" },
];

// ── TAB EMAIL EDITOR ─────────────────────────────────────────────
function TabEmailEditor() {
  const [empresaEmail, setEmpresaEmail]   = useState("qzsrl");
  const [templates, setTemplates]         = useState(null);
  const [seleccionado, setSeleccionado]   = useState(null);
  const [editando, setEditando]           = useState(null);
  const [vista, setVista]                 = useState("editor");
  const [guardado, setGuardado]           = useState(false);
  const [guardando, setGuardando]         = useState(false);
  const [generando, setGenerando]         = useState(false);
  const [brief, setBrief]                 = useState("");
  const [nuevoNombre, setNuevoNombre]     = useState("");
  const [nuevoTipo, setNuevoTipo]         = useState("presentacion");
  const [creando, setCreando]             = useState(false);

  const apiBase = "https://qzsrl.velqo.io";

  useEffect(() => {
    setTemplates(null);
    setSeleccionado(null);
    setEditando(null);
    fetch(`${apiBase}/api/templates/${empresaEmail}`)
      .then(r => r.json())
      .then(data => {
        const arr = Array.isArray(data) && data.length > 0 ? data : TEMPLATES_DEFAULT_QZ.filter(t => t.empresa === empresaEmail);
        setTemplates(arr);
        setSeleccionado(arr[0]?.id || null);
      })
      .catch(() => {
        setTemplates(TEMPLATES_DEFAULT_QZ.filter(t => t.empresa === empresaEmail));
        setSeleccionado(TEMPLATES_DEFAULT_QZ[0]?.id || null);
      });
  }, [empresaEmail]);

  const persistir = async (arr) => {
    setGuardando(true);
    try {
      await fetch(`${apiBase}/api/templates/${empresaEmail}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(arr),
      });
    } catch(e) {}
    setGuardando(false);
  };

  const tpl = editando || (templates||[]).find(t => t.id === seleccionado);

  const guardar = async () => {
    const updated = templates.map(t => t.id === tpl.id ? tpl : t);
    await persistir(updated);
    setTemplates(updated);
    setEditando(null);
    setGuardado(true);
    setTimeout(() => setGuardado(false), 2500);
  };

  const crearTemplate = async () => {
    if (!nuevoNombre.trim()) return;
    const id = "tpl_" + Date.now();
    const nuevo = { id, nombre: nuevoNombre.trim(), tipo: nuevoTipo, empresa: empresaEmail, html: "" };
    const updated = [...(templates||[]), nuevo];
    await persistir(updated);
    setTemplates(updated);
    setSeleccionado(id);
    setEditando(null);
    setCreando(false);
    setNuevoNombre("");
  };

  const eliminarTemplate = async (id) => {
    if (!window.confirm("¿Eliminar este template?")) return;
    const updated = (templates||[]).filter(t => t.id !== id);
    await persistir(updated);
    setTemplates(updated);
    setSeleccionado(updated[0]?.id || null);
    setEditando(null);
  };

  const generarConIA = async () => {
    if (!brief.trim()) return;
    setGenerando(true);
    try {
      const resp = await fetch(`${apiBase}/api/ai/generar-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ brief: brief.trim(), empresa: empresaEmail }),
      });
      const data = await resp.json();
      if (data.error) throw new Error(data.error);
      const html = data.html || "";
      setEditando({ ...tpl, html });
      setBrief("");
    } catch (e) {
      alert("Error al generar con IA: " + e.message);
    }
    setGenerando(false);
  };

  const tipoColor = { presentacion:"#3B82F6", "5preguntas":"#8B5CF6", seguimiento:"#F59E0B", custom:"#10B981" };
  const empColors = { qzsrl:"#FF6B00", lowcost:"#F59E0B", airquality:"#06B6D4" };

  if (templates === null) return (
    <div style={{ padding:60, textAlign:"center", color:"rgba(255,255,255,0.3)", fontSize:13 }}>
      Cargando templates...
    </div>
  );

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:18, color:"#fff", marginBottom:3 }}>✉️ Editor de emails</div>
          <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)" }}>Creá y editá los templates. Asigná el tipo para que Hermes los use automáticamente.</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {/* Selector empresa */}
          {[
            { key:"qzsrl",      label:"QZ SRL",      color:"#FF6B00" },
            { key:"lowcost",    label:"Quick Clean",     color:"#F59E0B" },
            { key:"airquality", label:"Air Quality",  color:"#06B6D4" },
          ].map(e => (
            <button key={e.key} onClick={() => setEmpresaEmail(e.key)} style={{
              padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer",
              background: empresaEmail===e.key ? e.color : "rgba(255,255,255,0.05)",
              color: empresaEmail===e.key ? "#0F0A00" : "rgba(255,255,255,0.4)",
              fontWeight: empresaEmail===e.key ? 700 : 400, fontSize:12,
            }}>{e.label}</button>
          ))}
          <button onClick={() => setCreando(true)} style={{
            padding:"9px 16px", borderRadius:8, border:"1px solid rgba(255,107,0,0.3)",
            background:"rgba(255,107,0,0.1)", color:"#FF6B00", fontSize:12, fontWeight:700, cursor:"pointer",
          }}>+ Nuevo</button>
        </div>
      </div>

      {/* Modal nuevo template */}
      {creando && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
          <div style={{ background:"#1F2937", border:"1px solid rgba(255,255,255,0.1)", borderRadius:16, padding:28, width:"100%", maxWidth:400 }}>
            <div style={{ fontSize:16, fontWeight:700, color:"#fff", marginBottom:20 }}>Nuevo template</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Nombre</label>
              <input value={nuevoNombre} onChange={e => setNuevoNombre(e.target.value)} placeholder="ej: Email seguimiento"
                style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box" }}/>
            </div>
            <div style={{ marginBottom:22 }}>
              <label style={{ fontSize:11, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:1, display:"block", marginBottom:6 }}>Tipo</label>
              <select value={nuevoTipo} onChange={e => setNuevoTipo(e.target.value)}
                style={{ width:"100%", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:13, outline:"none", boxSizing:"border-box" }}>
                {TIPOS_EMAIL_QZ.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => setCreando(false)} style={{ flex:1, padding:"10px", borderRadius:8, border:"1px solid rgba(255,255,255,0.1)", background:"transparent", color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:13 }}>Cancelar</button>
              <button onClick={crearTemplate} disabled={!nuevoNombre.trim()} style={{ flex:2, padding:"10px", borderRadius:8, border:"none", background: nuevoNombre.trim() ? "#FF6B00" : "rgba(255,255,255,0.1)", color: nuevoNombre.trim() ? "#0F0A00" : "rgba(255,255,255,0.3)", fontWeight:700, fontSize:13, cursor: nuevoNombre.trim() ? "pointer" : "not-allowed" }}>Crear template</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"240px 1fr", gap:16, alignItems:"start" }}>
        {/* Sidebar */}
        <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, overflow:"hidden" }}>
          <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:"0.1em", color:"rgba(255,255,255,0.3)" }}>Templates ({(templates||[]).length})</div>
          </div>
          {(templates||[]).map(t => (
            <div key={t.id} onClick={() => { setSeleccionado(t.id); setEditando(null); setVista("editor"); setGuardado(false); }} style={{
              padding:"13px 16px",
              background: seleccionado===t.id ? "rgba(255,107,0,0.1)" : "transparent",
              borderLeft: seleccionado===t.id ? "3px solid #FF6B00" : "3px solid transparent",
              cursor:"pointer", borderBottom:"1px solid rgba(255,255,255,0.04)",
            }}>
              <div style={{ fontSize:13, fontWeight: seleccionado===t.id ? 700 : 500, color:"#fff", marginBottom:4 }}>{t.nombre}</div>
              <span style={{ fontSize:9, fontWeight:700, padding:"2px 8px", borderRadius:10, background:`${tipoColor[t.tipo]||"#6B7280"}18`, color:tipoColor[t.tipo]||"#6B7280" }}>
                {TIPOS_EMAIL_QZ.find(x=>x.id===t.tipo)?.label||t.tipo}
              </span>
            </div>
          ))}
        </div>

        {/* Panel */}
        {tpl ? (
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {/* Barra superior */}
            <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:"14px 18px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" }}>
              <input value={tpl.nombre} onChange={e => setEditando({...tpl, nombre:e.target.value})} style={{ flex:1, minWidth:180, background:"transparent", border:"none", borderBottom:"1px solid rgba(255,255,255,0.15)", color:"#fff", fontSize:15, fontWeight:700, padding:"2px 0", outline:"none" }}/>
              <select value={tpl.tipo} onChange={e => setEditando({...tpl, tipo:e.target.value})} style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:7, padding:"6px 12px", color:"#fff", fontSize:12, outline:"none" }}>
                {TIPOS_EMAIL_QZ.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <div style={{ display:"flex", background:"rgba(255,255,255,0.06)", borderRadius:7, overflow:"hidden" }}>
                {["editor","preview"].map(v => (
                  <button key={v} onClick={() => setVista(v)} style={{ padding:"6px 14px", border:"none", cursor:"pointer", background:vista===v?"#FF6B00":"transparent", color:vista===v?"#0F0A00":"rgba(255,255,255,0.5)", fontSize:11, fontWeight:600 }}>
                    {v==="editor" ? "✏️ Código" : "👁 Preview"}
                  </button>
                ))}
              </div>
              {editando && (
                <div style={{ display:"flex", gap:6 }}>
                  <button onClick={() => setEditando(null)} style={{ padding:"6px 14px", borderRadius:7, border:"1px solid rgba(255,255,255,0.1)", background:"transparent", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:12 }}>Descartar</button>
                  <button onClick={guardar} disabled={guardando} style={{ padding:"6px 16px", borderRadius:7, border:"none", background:guardando?"rgba(255,255,255,0.1)":"#FF6B00", color:guardando?"rgba(255,255,255,0.3)":"#0F0A00", fontWeight:700, cursor:guardando?"not-allowed":"pointer", fontSize:12 }}>
                    {guardando ? "Guardando..." : "💾 Guardar"}
                  </button>
                </div>
              )}
              {guardado && <span style={{ fontSize:12, color:"#10B981", fontWeight:600 }}>✓ Guardado</span>}
              <button onClick={() => eliminarTemplate(tpl.id)} style={{ padding:"6px 10px", borderRadius:7, border:"1px solid rgba(239,68,68,0.2)", background:"rgba(239,68,68,0.07)", color:"#EF4444", cursor:"pointer", fontSize:11 }}>🗑</button>
            </div>

            {/* Asunto */}
            <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:"12px 18px", display:"flex", alignItems:"center", gap:10 }}>
              <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", fontWeight:700, whiteSpace:"nowrap" }}>ASUNTO</span>
              <input value={tpl.asunto || ""} onChange={e => setEditando({...tpl, asunto:e.target.value})} placeholder="Ej: Limpieza profesional para {{empresa}}" style={{ flex:1, background:"transparent", border:"none", borderBottom:"1px solid rgba(255,255,255,0.12)", color:"#fff", fontSize:13, padding:"4px 0", outline:"none" }}/>
              <span style={{ fontSize:10, color:"rgba(255,255,255,0.2)" }}>Variables: {"{{contacto}}"}, {"{{empresa}}"}</span>
            </div>

            {/* Editor */}
            {vista === "editor" && (
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, overflow:"hidden" }}>
                <div style={{ padding:"10px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.1em" }}>HTML · Gmail-compatible · tablas · estilos inline</span>
                </div>
                <textarea value={tpl.html} onChange={e => setEditando({...tpl, html:e.target.value})} spellCheck={false} style={{ width:"100%", height:360, background:"transparent", border:"none", padding:"16px 18px", color:"rgba(255,255,255,0.8)", fontSize:12, fontFamily:"'Courier New',monospace", resize:"vertical", outline:"none", boxSizing:"border-box", lineHeight:1.6 }}/>
              </div>
            )}

            {/* Preview */}
            {vista === "preview" && (
              <div style={{ background:"#e8e4de", borderRadius:12, padding:24 }}>
                <div style={{ fontSize:11, color:"rgba(0,0,0,0.4)", marginBottom:12, textTransform:"uppercase", letterSpacing:"0.1em" }}>Vista previa — Gmail</div>
                <iframe srcDoc={`<!DOCTYPE html><html><body style="margin:0;padding:0;">${tpl.html}</body></html>`} style={{ width:"100%", border:"none", borderRadius:8, minHeight:420 }} title="preview" sandbox="allow-same-origin"/>
              </div>
            )}

            {/* Generador IA */}
            <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:12, padding:18 }}>
              <div style={{ fontSize:13, fontWeight:700, color:"#fff", marginBottom:4 }}>✨ Generador con IA</div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:12 }}>Describí el email y la IA lo genera en el estilo de QZ SRL.</div>
              <div style={{ display:"flex", gap:10 }}>
                <textarea value={brief} onChange={e => setBrief(e.target.value)} placeholder='ej: "Email para hospitales privados presentando el servicio de limpieza de campanas, mencionando el riesgo de incendio y la certificación obligatoria"' style={{ flex:1, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, padding:"10px 14px", color:"#fff", fontSize:12, resize:"none", outline:"none", minHeight:72, lineHeight:1.6 }}/>
                <button onClick={generarConIA} disabled={generando||!brief.trim()} style={{ padding:"10px 20px", borderRadius:8, border:"none", background:generando||!brief.trim()?"rgba(255,255,255,0.08)":"#FF6B00", color:generando||!brief.trim()?"rgba(255,255,255,0.3)":"#0F0A00", fontWeight:700, fontSize:12, cursor:generando||!brief.trim()?"not-allowed":"pointer", whiteSpace:"nowrap", alignSelf:"stretch" }}>
                  {generando ? "⏳ Generando..." : "✨ Generar con IA"}
                </button>
              </div>
            </div>

            {/* Info tipo */}
            <div style={{ background:`${tipoColor[tpl.tipo]||"#6B7280"}0d`, border:`1px solid ${tipoColor[tpl.tipo]||"#6B7280"}25`, borderRadius:10, padding:"12px 16px" }}>
              <div style={{ fontSize:12, fontWeight:700, color:tipoColor[tpl.tipo]||"#6B7280", marginBottom:4 }}>
                📨 Tipo asignado: <strong>{TIPOS_EMAIL_QZ.find(x=>x.id===tpl.tipo)?.label||tpl.tipo}</strong>
              </div>
              <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>
                Hermes usará este template cuando envíe un email de tipo <code style={{ background:"rgba(0,0,0,0.2)", padding:"1px 6px", borderRadius:4 }}>{tpl.tipo}</code> para {empresaEmail}.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ padding:40, textAlign:"center", color:"rgba(255,255,255,0.3)", fontSize:13 }}>Seleccioná un template para editarlo</div>
        )}
      </div>
    </div>
  );
}

// ── KNOWLEDGE VIEWER ──────────────────────────────────────────────
function KnowledgeViewer({ empresa }) {
  const [data, setData] = useState({ texto:"", chars:0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`https://qzsrl.velqo.io/api/knowledge/${empresa}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [empresa]);

  const limpiar = () => {
    if (!window.confirm("¿Borrar todo el conocimiento de esta empresa?")) return;
    fetch(`https://qzsrl.velqo.io/api/knowledge/${empresa}/clear`, { method:"POST" })
      .then(() => setData({ texto:"", chars:0 }))
      .catch(() => {});
  };

  return (
    <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, color:"#fff" }}>Conocimiento actual</div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:2 }}>
            {data.chars > 0 ? `${data.chars} caracteres cargados` : "Sin conocimiento cargado aún"}
          </div>
        </div>
        {data.chars > 0 && (
          <button onClick={limpiar} style={{
            padding:"5px 12px", borderRadius:7, border:"1px solid rgba(239,68,68,0.3)",
            background:"rgba(239,68,68,0.08)", color:"#EF4444", fontSize:11, cursor:"pointer",
          }}>🗑 Limpiar</button>
        )}
      </div>
      {loading && <div style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Cargando...</div>}
      {!loading && data.chars === 0 && (
        <div style={{ fontSize:12, color:"rgba(255,255,255,0.2)", textAlign:"center", padding:"20px 0" }}>
          No hay conocimiento cargado. Subí un archivo arriba para empezar.
        </div>
      )}
      {!loading && data.chars > 0 && (
        <textarea
          readOnly
          value={data.texto}
          style={{
            width:"100%", height:200, background:"rgba(0,0,0,0.3)",
            border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
            padding:"10px 12px", color:"rgba(255,255,255,0.6)", fontSize:11,
            resize:"vertical", outline:"none", fontFamily:"monospace",
          }}
        />
      )}
    </div>
  );
}

// ── APP PRINCIPAL ────────────────────────────────────────────────
export default function App() {
  // ── Estados (TODOS primero) ──────────────────────────────────
  const isMobile = useIsMobile();
  const [usuario, setUsuario]           = useLocalStorage("qz_usuario", null);
  const [leads, setLeads]               = useState([]);
  const [leadsListo, setLeadsListo]     = useState(false);
  const [regiones, setRegiones]         = useLocalStorage("qz_regiones", REGIONES_INICIALES);
  const [tab, setTab]                   = useState("dashboard");
  const [filtroEmpresa, setFiltroEmpresa]     = useState("Todas");
  const [filtroRegion, setFiltroRegion]       = useState("Todas");
  const [filtroSegmento, setFiltroSegmento]   = useState("Todos");
  const [filtroVendedor, setFiltroVendedor]   = useState("Todos");
  const [busqueda, setBusqueda]         = useState("");
  const [dragId, setDragId]             = useState(null);
  const [modal, setModal]               = useState(null);
  const [configs, setConfigs]           = useState({});
  const [configEmpresa, setConfigEmpresa]     = useState("qzsrl");
  const [configEditando, setConfigEditando]   = useState(null);
  const [pendientes, setPendientes]     = useState({});
  const [sysStatus, setSysStatus]       = useState(null);
  const [smtpConfig, setSmtpConfig]     = useState({ email:"", password:"", host:"", port:"", modo:"" });
  const [smtpStatus, setSmtpStatus]     = useState(null);
  const [schedule, setSchedule]         = useState({ dias_busqueda:[1,3], dias_envio:[2,4], hora_envio:"09:00", sistema_activo:false, envio_automatico:false });
  const [estadoSistema, setEstadoSistema] = useState(null);
  const [vendedores, setVendedores]     = useState(VENDEDORES);
  const [nuevoVendedorNombre, setNuevoVendedorNombre] = useState("");

  // ── Constantes ───────────────────────────────────────────────
  const esAdmin = usuario?.rol === "admin";

  // ── Hooks de datos ───────────────────────────────────────────
  useLeadsFromFile(setLeads, setLeadsListo);

  // Migración: limpiar caché vieja de leads en localStorage (se usaba antes, pesaba varios MB)
  useEffect(() => { try { localStorage.removeItem("qz_leads"); } catch {} }, []);

  useEffect(() => {
    fetch(`https://qzsrl.velqo.io/api/vendedores/qzsrl`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length > 0) setVendedores(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!esAdmin) return;
    const empresas = ["qzsrl", "lowcost", "airquality"];
    empresas.forEach(emp => {
      fetch(`https://qzsrl.velqo.io/api/config/${emp}`)
        .then(r => r.json())
        .then(data => setConfigs(prev => ({ ...prev, [emp]: data })))
        .catch(() => {});
      fetch(`https://qzsrl.velqo.io/api/pendientes/${emp}`)
        .then(r => r.json())
        .then(data => setPendientes(prev => ({ ...prev, [emp]: data.pendientes || 0 })))
        .catch(() => {});
    });
    fetch(`https://qzsrl.velqo.io/api/smtp/${configEmpresa}`)
      .then(r => r.json())
      .then(data => { if (data.email) setSmtpConfig(data); })
      .catch(() => {});
    fetch(`https://qzsrl.velqo.io/api/schedule/${configEmpresa}`)
      .then(r => r.json())
      .then(data => setSchedule(data))
      .catch(() => {});
    fetch(`https://qzsrl.velqo.io/api/estado-sistema/${configEmpresa}`)
      .then(r => r.json())
      .then(data => setEstadoSistema(data))
      .catch(() => {});
  }, [esAdmin, configEmpresa]);

  useEffect(() => {
    const fetchStatus = () => {
      fetch("https://qzsrl.velqo.io/api/status")
        .then(r => r.json())
        .then(data => setSysStatus(data))
        .catch(() => {});
    };
    fetchStatus();
    const iv = setInterval(fetchStatus, 2 * 60 * 1000);
    return () => clearInterval(iv);
  }, []);

  // Filtrar leads según rol y filtros
  const leadsFiltrados = leads.filter(l => {
    if (!esAdmin && l.vendedor !== usuario?.id) return false;
    if (filtroEmpresa !== "Todas" && l.empresa_captio !== filtroEmpresa) return false;
    if (filtroRegion !== "Todas" && l.region !== filtroRegion) return false;
    if (filtroSegmento !== "Todos" && l.segmento !== filtroSegmento) return false;
    if (filtroVendedor !== "Todos" && l.vendedor !== filtroVendedor) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      return l.empresa.toLowerCase().includes(q) || l.contacto.toLowerCase().includes(q);
    }
    return true;
  });

  // KPIs
  const pipeline    = leads.filter(l=>l.estado==="Cotizado").reduce((a,b)=>a+b.monto,0);
  const ganado      = leads.filter(l=>l.estado==="Ganado").reduce((a,b)=>a+b.monto,0);
  const totalCerr   = leads.filter(l=>l.estado==="Ganado"||l.estado==="Perdido").length;
  const tasaCierre  = totalCerr ? Math.round(leads.filter(l=>l.estado==="Ganado").length/totalCerr*100) : 0;
  const nuevosHoy   = leads.filter(l=>l.fecha===new Date().toISOString().split("T")[0]).length;
  const leadsGanados = leads.filter(l=>l.estado==="Ganado"&&l.monto>0);
  const valorPromedio = leadsGanados.length ? Math.round(leadsGanados.reduce((a,b)=>a+b.monto,0)/leadsGanados.length) : 0;
  const leadsValidados = leads.filter(l=>l.estado==="Validado").length;

  // Drag & Drop
  const handleDragStart = useCallback((e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragOver = useCallback(e => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }, []);

  const handleDrop = useCallback((e, colId) => {
    e.preventDefault();
    if (!dragId) return;
    const lead = leads.find(l => l.id === dragId);
    if (!lead || lead.estado === colId) { setDragId(null); return; }
    setModal({ tipo:"mover", lead, destino:colId });
    setDragId(null);
  }, [dragId, leads]);

  const confirmarMover = ({ monto, motivo }) => {
    const { lead, destino } = modal;
    const nuevosLeads = leads.map(l =>
      l.id === lead.id
        ? { ...l, estado:destino, monto: monto || l.monto, notas: motivo ? `Perdido: ${motivo}` : l.notas }
        : l
    );
    setLeads(nuevosLeads);
    // Persistir en el servidor
    fetch("https://qzsrl.velqo.io/api/leads", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(nuevosLeads.map(l => ({
        ...l,
        cargo_decisor: l.cargo,
        email_estimado: l.email,
        telefono: l.tel,
      })))
    }).catch(() => {});
    setModal(null);
  };

  const asignarVendedor = (leadId, vendedorId) => {
    setLeads(prev => prev.map(l => l.id===leadId ? {...l, vendedor:vendedorId} : l));
  };

  const toggleSistema = () => {
    const nuevoEstado = !schedule.sistema_activo;
    if (!window.confirm(nuevoEstado
      ? "¿Reactivar la búsqueda y el envío automático de emails?"
      : "¿Pausar la búsqueda y el envío automático de emails? El dashboard sigue funcionando normalmente."
    )) return;
    setSchedule(prev => {
      const nuevo = { ...prev, sistema_activo: nuevoEstado };
      fetch(`https://qzsrl.velqo.io/api/schedule/${configEmpresa}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(nuevo),
      }).catch(() => {});
      return nuevo;
    });
  };

  // Segmentos únicos
  const segmentos = ["Todos", ...Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)))];

  if (!usuario) return <Login vendedores={vendedores} onLogin={setUsuario}/>;

  const tabs = [
    { id:"dashboard",    label:"📊 Dashboard" },
    { id:"kanban",       label:"🗂 Pipeline" },
    { id:"leads",        label:"📋 Leads" },
    { id:"actividad",    label:"⚡ Actividad" },
    { id:"emails",       label:"✉️ Emails" },
    ...(esAdmin ? [
      { id:"config",  label:"🎯 Configuración" },
      { id:"admin",   label:"⚙️ Admin" },
    ] : []),
  ];

  return (
    <div style={{ minHeight:"100vh", background:"#0F0F0F", fontFamily:"'DM Sans','Segoe UI',sans-serif", color:"#fff" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&display=swap');
        *{box-sizing:border-box} ::-webkit-scrollbar{width:4px;height:4px}
        ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:2px}
        select option{background:#1a2234}
        @media(max-width:640px){.kanban-grid{flex-direction:column!important}}
      `}</style>

      {/* HEADER */}
      <div style={{
        height:56, padding:"0 20px", borderBottom:"1px solid rgba(255,255,255,0.06)",
        display:"flex", alignItems:"center", justifyContent:"space-between",
        background:"rgba(15,15,15,0.97)", position:"sticky", top:0, zIndex:50,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{
            width:32, height:32, borderRadius:9,
            background:"linear-gradient(135deg,#FF6B00,#CC4400)",
            display:"flex", alignItems:"center", justifyContent:"center",
            fontWeight:900, fontSize:13, color:"#fff",
          }}>QZ</div>
          <span style={{ fontWeight:800, fontSize:14, color:"#fff" }}>CRM</span>
          {nuevosHoy > 0 && (
            <span style={{ fontSize:10, fontWeight:700, background:"rgba(239,68,68,0.2)", color:"#EF4444", padding:"2px 8px", borderRadius:20 }}>
              +{nuevosHoy} hoy
            </span>
          )}
        </div>

        <div style={{ display:"flex", gap:2 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding:"5px 14px", borderRadius:7, border:"none",
              background: tab===t.id ? "rgba(255,107,0,0.15)" : "transparent",
              color: tab===t.id ? "#FF6B00" : "rgba(255,255,255,0.4)",
              fontWeight: tab===t.id ? 700 : 500, fontSize:12, cursor:"pointer",
            }}>{t.label}</button>
          ))}
        </div>

        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {esAdmin && (
            <button onClick={toggleSistema} title="Pausa/reactiva la búsqueda y el envío automático" style={{
              display:"flex", alignItems:"center", gap:6,
              padding:"4px 10px", borderRadius:20, border:"1px solid rgba(255,255,255,0.08)",
              background: schedule.sistema_activo ? "rgba(16,185,129,0.1)" : "rgba(239,68,68,0.1)",
              color: schedule.sistema_activo ? "#10B981" : "#EF4444",
              cursor:"pointer", fontSize:11, fontWeight:700,
            }}>
              <span style={{ width:7, height:7, borderRadius:"50%", background: schedule.sistema_activo ? "#10B981" : "#EF4444" }}/>
              {schedule.sistema_activo ? "Sistema activo" : "Sistema pausado"}
            </button>
          )}
          <span style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>{usuario.nombre}</span>
          <button onClick={() => setUsuario(null)} style={{
            padding:"4px 10px", borderRadius:6, border:"1px solid rgba(255,255,255,0.08)",
            background:"transparent", color:"rgba(255,255,255,0.35)", cursor:"pointer", fontSize:11,
          }}>Salir</button>
        </div>
      </div>

      <div style={{ padding:"20px 16px", maxWidth:1400, margin:"0 auto" }}>

        {/* ── LEADS ── */}
        {tab === "leads" && (
          <TabLeads leads={leads} />
        )}

        {/* ── ACTIVIDAD ── */}
        {tab === "actividad" && (
          <TabActividad leads={leads} esAdmin={esAdmin} />
        )}

        {/* ── EMAILS ── */}
        {tab === "emails" && esAdmin && (
          <EmailSection esAdmin={esAdmin} schedule={schedule} setSchedule={setSchedule} leads={leads} />
        )}

        {/* ── KANBAN ── */}
        {tab === "kanban" && (
          <>
            {/* Filtros */}
            <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap" }}>
              <input
                value={busqueda}
                onChange={e=>setBusqueda(e.target.value)}
                placeholder="🔍 Buscar empresa..."
                style={{
                  flex:1, minWidth:160, background:"rgba(255,255,255,0.05)",
                  border:"1px solid rgba(255,255,255,0.08)", borderRadius:8,
                  padding:"8px 12px", color:"#fff", fontSize:12, outline:"none",
                }}
              />
              {[
                { val:filtroEmpresa,   set:setFiltroEmpresa,   opts:EMPRESAS_CAPTIO,          label:"Empresa" },
                { val:filtroRegion,    set:setFiltroRegion,    opts:["Todas",...regiones,"Otros"], label:"Región" },
                { val:filtroSegmento,  set:setFiltroSegmento,  opts:segmentos,                label:"Rubro"  },
                ...(esAdmin ? [{ val:filtroVendedor, set:setFiltroVendedor, opts:["Todos",...vendedores.filter(v=>v.rol==="vendedor").map(v=>v.id)], label:"Vendedor" }] : []),
              ].map(f => (
                <select key={f.label} value={f.val} onChange={e=>f.set(e.target.value)} style={{
                  background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.08)",
                  borderRadius:8, padding:"8px 12px", color:"#fff", fontSize:12, outline:"none",
                }}>
                  {f.opts.map(o => (
                    <option key={o} value={o}>
                      {f.label==="Vendedor" ? (vendedores.find(v=>v.id===o)?.nombre || "Todos") : o}
                    </option>
                  ))}
                </select>
              ))}
            </div>

            {/* Columnas kanban */}
            <div className="kanban-grid" style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
              {COLUMNAS.map(col => (
                <Columna
                  key={col.id}
                  col={col}
                  leads={leadsFiltrados.filter(l=>l.estado===col.id)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onTap={lead => setModal({ tipo:"detalle", lead })}
                  esAdmin={esAdmin}
                  vendedores={vendedores}
                />
              ))}
            </div>
          </>
        )}

        {/* ── DASHBOARD NASA ── */}
        {tab === "dashboard" && !leadsListo && (
          <div style={{ textAlign:"center", padding:60, color:"rgba(255,255,255,0.3)", fontSize:13 }}>Cargando leads...</div>
        )}
        {tab === "dashboard" && leadsListo && (
          <div>
            {/* Fila 1 — KPIs grandes */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:10, marginBottom:20 }}>
              {[
                { icon:"🆕", label:"Leads totales",    value:leads.length,          color:"#8B5CF6", sub:"en el sistema" },
                { icon:"✅", label:"Validados",         value:leadsValidados,        color:"#3B82F6", sub:"con interés confirmado" },
                { icon:"💬", label:"Cotizados",         value:leads.filter(l=>l.estado==="Cotizado").length, color:"#F59E0B", sub:"esperando respuesta" },
                { icon:"🏆", label:"Ganados",           value:leads.filter(l=>l.estado==="Ganado").length,  color:"#10B981", sub:"cierres exitosos" },
                { icon:"💰", label:"Pipeline $",        value:fmt$(pipeline),        color:"#F59E0B", sub:"en cotizaciones" },
                { icon:"📈", label:"Closing rate",      value:`${tasaCierre}%`,      color:"#FF6B00", sub:"tasa de cierre" },
                { icon:"⭐", label:"Valor promedio",    value:fmt$(valorPromedio),   color:"#10B981", sub:"por operación ganada" },
                { icon:"🔥", label:"Nuevos hoy",        value:nuevosHoy,             color:"#EF4444", sub:"leads del día" },
              ].map(k => (
                <div key={k.label} style={{
                  background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
                  borderRadius:12, padding:"14px 16px", position:"relative", overflow:"hidden",
                }}>
                  <div style={{ position:"absolute", top:8, right:10, fontSize:20, opacity:0.15 }}>{k.icon}</div>
                  <div style={{ fontSize:24, fontWeight:900, color:k.color, lineHeight:1 }}>{k.value}</div>
                  <div style={{ fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.7)", marginTop:6 }}>{k.label}</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginTop:2 }}>{k.sub}</div>
                </div>
              ))}
            </div>

            {/* Fila 2 — Gráficos */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:14 }}>

              {/* Pipeline por empresa */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"#fff" }}>Pipeline por empresa</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:14 }}>Leads activos por proyecto</div>
                {["QZ SRL","Quick Clean","Air Quality"].map((emp, ei) => {
                  const cols = ["#FF6B00","#F59E0B","#06B6D4"];
                  const eLeads = leads.filter(l=>l.empresa_captio===emp);
                  const ePct = leads.length ? Math.round(eLeads.length/leads.length*100) : 0;
                  const eMonto = eLeads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0);
                  return (
                    <div key={emp} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.7)", fontWeight:500 }}>{emp}</span>
                        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                          {eMonto>0 && <span style={{ fontSize:10, color:cols[ei], fontWeight:700 }}>{fmt$(eMonto)}</span>}
                          <span style={{ fontSize:11, fontWeight:700, color:cols[ei] }}>{eLeads.length} leads</span>
                        </div>
                      </div>
                      <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                        <div style={{ height:"100%", width:`${Math.max(ePct,2)}%`, background:cols[ei], borderRadius:3, transition:"width 0.5s" }}/>
                      </div>
                      <div style={{ display:"flex", gap:6, marginTop:5 }}>
                        {["Nuevo","Validado","Cotizado","Ganado"].map(est => {
                          const n = eLeads.filter(l=>l.estado===est).length;
                          if (!n) return null;
                          const estCol = {Nuevo:"#6B7280",Validado:"#3B82F6",Cotizado:"#F59E0B",Ganado:"#10B981"}[est];
                          return <span key={est} style={{ fontSize:9, color:estCol, background:`${estCol}20`, padding:"1px 6px", borderRadius:10 }}>{est}: {n}</span>;
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Leads por ciudad */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"#fff" }}>Leads por ciudad</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:14 }}>Distribución geográfica</div>
                {(() => {
                  const ciudades = ["CABA","Córdoba","Rosario","Mendoza","Santa Fe"];
                  const cols = ["#8B5CF6","#FF6B00","#06B6D4","#10B981","#F59E0B"];
                  const otrosLeads = leads.filter(l=>!ciudades.some(c=>l.region===c||l.ciudad?.includes(c)));
                  const items = [
                    ...ciudades.map((ciudad,ci) => ({
                      label: ciudad,
                      n: leads.filter(l=>l.region===ciudad||l.ciudad?.includes(ciudad)).length,
                      color: cols[ci],
                    })),
                    { label:"Otros", n: otrosLeads.length, color:"#4B5563" },
                  ].filter(x=>x.n>0);
                  const max = Math.max(...items.map(x=>x.n), 1);
                  return items.map(({ label, n, color }) => (
                    <div key={label} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.65)" }}>{label}</span>
                        <span style={{ fontSize:12, fontWeight:700, color }}>{n}</span>
                      </div>
                      <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                        <div style={{ height:"100%", width:`${Math.round(n/max*100)}%`, background:color, borderRadius:3, transition:"width 0.5s" }}/>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Fila 3 — Rankings */}
            <div style={{ display:"grid", gridTemplateColumns:esAdmin?"1fr 1fr 1fr":"1fr 1fr", gap:14 }}>

              {/* Por segmento/industria */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"#fff" }}>Por segmento</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:14 }}>Dónde está el negocio</div>
                {(() => {
                  const segs = Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)));
                  const segData = segs.map(s => ({
                    label: s,
                    total: leads.filter(l=>l.segmento===s).length,
                    ganados: leads.filter(l=>l.segmento===s&&l.estado==="Ganado").length,
                    monto: leads.filter(l=>l.segmento===s&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                  })).sort((a,b)=>b.total-a.total).slice(0,6);
                  const max = Math.max(...segData.map(x=>x.total),1);
                  const segCols = ["#8B5CF6","#FF6B00","#06B6D4","#10B981","#F59E0B","#EF4444"];
                  return segData.map(({ label, total, ganados, monto },si) => (
                    <div key={label} style={{ marginBottom:12 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:11, color:"rgba(255,255,255,0.65)", maxWidth:110, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{label}</span>
                        <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                          {ganados>0 && <span style={{ fontSize:9, color:"#10B981" }}>✓{ganados}</span>}
                          {monto>0 && <span style={{ fontSize:9, color:"#F59E0B" }}>{fmt$(monto)}</span>}
                          <span style={{ fontSize:11, fontWeight:700, color:segCols[si%6] }}>{total}</span>
                        </div>
                      </div>
                      <div style={{ height:5, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
                        <div style={{ height:"100%", width:`${Math.round(total/max*100)}%`, background:segCols[si%6], borderRadius:2 }}/>
                      </div>
                    </div>
                  ));
                })()}
              </div>

              {/* Top leads por score */}
              <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"#fff" }}>Top leads por score</div>
                <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:14 }}>Las mejores oportunidades</div>
                {[...leads].filter(l=>l.estado==="Nuevo"||l.estado==="Validado").sort((a,b)=>b.score-a.score).slice(0,6).map(l => (
                  <div key={l.id} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"8px 10px",
                    background:"rgba(255,255,255,0.03)", borderRadius:8, marginBottom:6,
                    cursor:"pointer",
                  }} onClick={() => setModal({ tipo:"detalle", lead:l })}>
                    <div style={{
                      width:32, height:32, borderRadius:8, background:`${scoreColor(l.score)}20`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:12, fontWeight:900, color:scoreColor(l.score), flexShrink:0,
                    }}>{l.score}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</div>
                      <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>{l.ciudad} · {l.segmento}</div>
                    </div>
                    <span style={{ fontSize:9, padding:"2px 7px", borderRadius:10, background:`${COLUMNAS.find(c=>c.id===l.estado)?.color}20`, color:COLUMNAS.find(c=>c.id===l.estado)?.color, flexShrink:0 }}>{l.estado}</span>
                  </div>
                ))}
              </div>

              {/* Ranking vendedores — solo admin */}
              {esAdmin && (
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
                  <div style={{ fontWeight:700, fontSize:13, marginBottom:4, color:"#fff" }}>Ranking vendedores</div>
                  <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:14 }}>Rendimiento del equipo</div>
                  {vendedores.filter(v=>v.rol==="vendedor").map((v,i) => {
                    const vLeads   = leads.filter(l=>l.vendedor===v.id);
                    const vGanados = vLeads.filter(l=>l.estado==="Ganado");
                    const vMonto   = vGanados.reduce((a,b)=>a+b.monto,0);
                    const vRate    = vLeads.filter(l=>l.estado==="Ganado"||l.estado==="Perdido").length
                      ? Math.round(vGanados.length/vLeads.filter(l=>l.estado==="Ganado"||l.estado==="Perdido").length*100) : 0;
                    const medal = ["🥇","🥈","🥉"][i] || "·";
                    return (
                      <div key={v.id} style={{
                        padding:"12px 14px", background:"rgba(255,255,255,0.03)",
                        borderRadius:10, marginBottom:10,
                        border: i===0 ? "1px solid rgba(255,107,0,0.3)" : "1px solid transparent",
                      }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                          <span style={{ fontSize:18 }}>{medal}</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>{v.nombre}</div>
                            <div style={{ fontSize:10, color:"rgba(255,255,255,0.35)" }}>{vLeads.length} leads · {vRate}% cierre</div>
                          </div>
                          <div style={{ fontSize:14, fontWeight:800, color:"#10B981" }}>{fmt$(vMonto)}</div>
                        </div>
                        <div style={{ display:"flex", gap:6 }}>
                          {["Nuevo","Validado","Cotizado","Ganado","Perdido"].map(est => {
                            const n = vLeads.filter(l=>l.estado===est).length;
                            if(!n) return null;
                            const ec = {Nuevo:"#6B7280",Validado:"#3B82F6",Cotizado:"#F59E0B",Ganado:"#10B981",Perdido:"#EF4444"}[est];
                            return <span key={est} style={{ fontSize:9, color:ec, background:`${ec}15`, padding:"2px 7px", borderRadius:10 }}>{est[0]}: {n}</span>;
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── CONFIGURACIÓN ── */}
        {tab === "config" && esAdmin && (() => {
          const cfg = configEditando || configs[configEmpresa] || {};
          const empColor = {qzsrl:"#FF6B00",lowcost:"#F59E0B",airquality:"#06B6D4"}[configEmpresa] || "#FF6B00";

          const guardarConfig = () => {
            fetch(`https://qzsrl.velqo.io/api/config/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(cfg, null, 2),
            }).then(() => {
              setConfigs(prev => ({ ...prev, [configEmpresa]: cfg }));
              setConfigEditando(null);
              alert("Configuración guardada");
            }).catch(() => alert("Error al guardar"));
          };

          const DIAS = ["D","L","M","X","J","V","S"];
          const MAX_DIAS_PLAN = 2;

          const toggleDia = (key, idx) => {
            const arr = [...(schedule[key] || [])];
            const pos = arr.indexOf(idx);
            if (pos >= 0) {
              arr.splice(pos, 1);
            } else {
              if (arr.length >= MAX_DIAS_PLAN) {
                alert(`Tu plan permite hasta ${MAX_DIAS_PLAN} días para esta acción. Para más volumen, hay que pasar a un plan superior.`);
                return;
              }
              arr.push(idx);
            }
            setSchedule(prev => ({ ...prev, [key]: arr.sort() }));
          };

          const guardarSchedule = () => {
            fetch(`https://qzsrl.velqo.io/api/schedule/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(schedule),
            }).then(() => alert("Horario guardado"))
              .catch(() => alert("Error al guardar horario"));
          };

          const MAX_VENDEDORES_PLAN = 3;

          const generarPin = () => String(Math.floor(1000 + Math.random()*9000));

          const persistirVendedores = (lista) => {
            fetch(`https://qzsrl.velqo.io/api/vendedores/qzsrl`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(lista),
            }).catch(() => alert("Error al guardar usuarios"));
          };

          const agregarVendedor = () => {
            const nombre = nuevoVendedorNombre.trim();
            if (!nombre) return;
            if (vendedores.length >= MAX_VENDEDORES_PLAN) return;
            const nuevo = { id: `v${Date.now()}`, nombre, pin: generarPin(), rol: "vendedor" };
            const lista = [...vendedores, nuevo];
            setVendedores(lista);
            persistirVendedores(lista);
            setNuevoVendedorNombre("");
          };

          const eliminarVendedor = (id) => {
            if (!window.confirm("¿Eliminar este usuario?")) return;
            const lista = vendedores.filter(v => v.id !== id);
            setVendedores(lista);
            persistirVendedores(lista);
          };

          const PROVEEDORES_SMTP = [
            { label:"Gmail",         host:"smtp.gmail.com",      port:"465", modo:"ssl"      },
            { label:"IONOS",         host:"smtp.ionos.es",       port:"587", modo:"starttls" },
            { label:"Outlook",       host:"smtp.office365.com",  port:"587", modo:"starttls" },
            { label:"Yahoo",         host:"smtp.mail.yahoo.com", port:"465", modo:"ssl"      },
            { label:"Hostinger",     host:"smtp.hostinger.com",  port:"465", modo:"ssl"      },
            { label:"OVH",           host:"ssl0.ovh.net",        port:"465", modo:"ssl"      },
            { label:"Otro / manual", host:"",                    port:"587", modo:"starttls" },
          ];

          const smtpDetectado = PROVEEDORES_SMTP.find(p => p.host === smtpConfig.host) || null;

          const guardarSmtp = () => {
            setSmtpStatus("saving");
            const payload = { ...smtpConfig, ...(smtpDetectado||{}) };
            fetch(`https://qzsrl.velqo.io/api/smtp/${configEmpresa}`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(payload),
            }).then(r => r.json())
              .then(d => setSmtpStatus(d.ok ? "ok" : "error"))
              .catch(() => setSmtpStatus("error"));
          };

          const probarSmtp = () => {
            setSmtpStatus("testing");
            const payload = { ...smtpConfig, ...(smtpDetectado||{}) };
            fetch(`https://qzsrl.velqo.io/api/smtp/${configEmpresa}/test`, {
              method:"POST", headers:{"Content-Type":"application/json"},
              body: JSON.stringify(payload),
            }).then(r => r.json())
              .then(d => setSmtpStatus(d.ok ? "ok" : "error"))
              .catch(() => setSmtpStatus("error"));
          };

          const sCard = {
            background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
            borderRadius:14, padding:20, marginBottom:16,
          };
          const sLabel = { fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:5, display:"block" };
          const sInput = {
            width:"100%", border:"1px solid rgba(255,255,255,0.1)", borderRadius:8,
            padding:"8px 12px", fontSize:13, color:"#fff", outline:"none",
            background:"rgba(255,255,255,0.05)", boxSizing:"border-box",
          };
          const sBtn = (primary) => ({
            padding:"9px 18px", borderRadius:8, border: primary ? "none" : "1px solid rgba(255,255,255,0.2)",
            background: primary ? empColor : "transparent",
            color: primary ? "#0F0A00" : "rgba(255,255,255,0.7)",
            fontWeight:600, fontSize:13, cursor:"pointer",
          });

          const PAISES_DISPONIBLES = ["España","Francia","Italia","Alemania","Portugal","Reino Unido","México","Argentina","Colombia","Chile","China","EEUU"];
          const paisesActivos = cfg.paises || ["España"];

          const togglePais = (p) => {
            const arr = paisesActivos.includes(p) ? paisesActivos.filter(x=>x!==p) : [...paisesActivos, p];
            setConfigEditando({...cfg, paises: arr});
          };

          return (
            <div style={{ maxWidth:"100%" }}>
              {/* Header con semáforo */}
              <div style={{ marginBottom:20, display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div>
                  <div style={{ fontWeight:800, fontSize:18, color:"#fff", marginBottom:4 }}>Configuración</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)" }}>Todo lo que el agente necesita para trabajar de forma autónoma</div>
                </div>
                {/* Semáforo estado sistema */}
                <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"10px 14px", minWidth:200 }}>
                  <div style={{ fontWeight:700, fontSize:11, color:"rgba(255,255,255,0.5)", marginBottom:8, textTransform:"uppercase", letterSpacing:0.5 }}>Estado del sistema</div>
                  {estadoSistema ? (
                    <>
                      {["envios","respuestas","busqueda","calificacion"].map(motor => {
                        const info = estadoSistema[motor];
                        if (!info) return (
                          <div key={motor} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:"#4B5563", display:"inline-block" }} />
                            <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{motor}: sin datos</span>
                          </div>
                        );
                        const horas = (Date.now() - new Date(info.fecha).getTime()) / 3600000;
                        const color = !info.ok ? "#EF4444" : horas > 48 ? "#F59E0B" : "#10B981";
                        const label = !info.ok ? "error" : horas > 48 ? "inactivo" : "ok";
                        return (
                          <div key={motor} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
                            <span style={{ width:8, height:8, borderRadius:"50%", background:color, display:"inline-block", boxShadow:`0 0 4px ${color}` }} />
                            <span style={{ fontSize:11, color:"#fff" }}>{motor}</span>
                            <span style={{ fontSize:10, color:"rgba(255,255,255,0.4)", marginLeft:"auto" }}>{label}</span>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)" }}>Cargando...</div>
                  )}
                </div>
              </div>

              {/* Selector empresa */}
              <div style={{ display:"flex", gap:8, marginBottom:20 }}>
                {[
                  { key:"qzsrl",      label:"QZ SRL",      color:"#FF6B00" },
                  { key:"lowcost",    label:"Quick Clean",  color:"#F59E0B" },
                  { key:"airquality", label:"Air Quality",  color:"#06B6D4" },
                ].map(e => (
                  <button key={e.key} onClick={() => { setConfigEmpresa(e.key); setConfigEditando(null); setSmtpStatus(null); }} style={{
                    padding:"8px 18px", borderRadius:8, border:"none", cursor:"pointer",
                    background: configEmpresa===e.key ? e.color : "rgba(255,255,255,0.05)",
                    color: configEmpresa===e.key ? "#0F0A00" : "rgba(255,255,255,0.5)",
                    fontWeight: configEmpresa===e.key ? 700 : 400, fontSize:13,
                  }}>{e.label}</button>
                ))}
              </div>

              {/* FILA 1: SEGMENTOS + EMAIL */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>

                {/* SECCIÓN 1: SEGMENTOS */}
                <div style={{...sCard, marginBottom:0}}>
                  <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:4 }}>1. Segmentos de búsqueda</div>
                  <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:14 }}>Dónde y a quién busca el agente.</div>

                  {/* Países */}
                  <label style={sLabel}>PAÍSES</label>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:14 }}>
                    {PAISES_DISPONIBLES.map(p => (
                      <button key={p} onClick={() => togglePais(p)} style={{
                        padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:500,
                        border: paisesActivos.includes(p) ? "none" : "1px solid rgba(255,255,255,0.15)",
                        background: paisesActivos.includes(p) ? empColor : "transparent",
                        color: paisesActivos.includes(p) ? "#0F0A00" : "rgba(255,255,255,0.45)",
                      }}>{p}</button>
                    ))}
                  </div>

                  {/* Sectores */}
                  <label style={sLabel}>SECTORES — el agente rota entre ellos</label>
                  {(cfg.segmentos||[]).map((seg, si) => (
                    <div key={si} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={seg} onChange={e => { const s=[...(cfg.segmentos||[])]; s[si]=e.target.value; setConfigEditando({...cfg,segmentos:s}); }} style={{...sInput, flex:1}} />
                      <button onClick={() => { const s=(cfg.segmentos||[]).filter((_,i)=>i!==si); setConfigEditando({...cfg,segmentos:s}); }} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:18, padding:"0 4px" }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setConfigEditando({...cfg, segmentos:[...(cfg.segmentos||[]),""] })}
                    style={{ marginTop:2, marginBottom:14, padding:"4px 12px", borderRadius:6, border:`1px dashed ${empColor}60`, background:"none", color:empColor, cursor:"pointer", fontSize:12 }}>+ Agregar sector</button>

                  {/* Ciudades */}
                  <label style={sLabel}>CIUDADES — el agente rota entre ellas</label>
                  {(cfg.regiones||[]).map((reg, ri) => (
                    <div key={ri} style={{ display:"flex", gap:6, marginBottom:6 }}>
                      <input value={reg} onChange={e => { const r=[...(cfg.regiones||[])]; r[ri]=e.target.value; setConfigEditando({...cfg,regiones:r}); }} style={{...sInput, flex:1}} />
                      <button onClick={() => { const r=(cfg.regiones||[]).filter((_,i)=>i!==ri); setConfigEditando({...cfg,regiones:r}); }} style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:18, padding:"0 4px" }}>×</button>
                    </div>
                  ))}
                  <button onClick={() => setConfigEditando({...cfg, regiones:[...(cfg.regiones||[]),""] })}
                    style={{ marginTop:2, marginBottom:14, padding:"4px 12px", borderRadius:6, border:`1px dashed ${empColor}60`, background:"none", color:empColor, cursor:"pointer", fontSize:12 }}>+ Agregar ciudad</button>

                  {/* Score + Decisor */}
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
                    <div>
                      <label style={sLabel}>Score mínimo</label>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <input type="range" min="0" max="100" value={cfg.score_minimo||65}
                          onChange={e => setConfigEditando({...cfg, score_minimo:parseInt(e.target.value)})} style={{ flex:1 }} />
                        <span style={{ fontWeight:700, fontSize:15, color:empColor, minWidth:28 }}>{cfg.score_minimo||65}</span>
                      </div>
                    </div>
                    <div>
                      <label style={sLabel}>Decisor objetivo</label>
                      <input value={cfg.decisor||""} onChange={e => setConfigEditando({...cfg,decisor:e.target.value})} style={sInput} />
                    </div>
                  </div>

                  <div style={{ marginBottom:12 }}>
                    <label style={sLabel}>Descripción del servicio</label>
                    <textarea value={cfg.servicio||""} onChange={e => setConfigEditando({...cfg,servicio:e.target.value})}
                      style={{...sInput, minHeight:52, resize:"vertical"}} />
                  </div>

                  {configEditando && (
                    <div style={{ display:"flex", gap:8 }}>
                      <button onClick={guardarConfig} style={sBtn(true)}>Guardar</button>
                      <button onClick={() => setConfigEditando(null)} style={sBtn(false)}>Cancelar</button>
                    </div>
                  )}
                </div>

                {/* SECCIÓN 2+3: DÍAS DE BÚSQUEDA + EMAIL */}
                <div style={{...sCard, marginBottom:0}}>
                  <div style={{ marginBottom:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:4 }}>2. Días de búsqueda</div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:4 }}>El agente buscará nuevas empresas estos días.</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:12, fontStyle:"italic" }}>Plan base: hasta {MAX_DIAS_PLAN} días. Más volumen requiere upgrade.</div>
                    <div style={{ display:"flex", gap:6, marginBottom:14 }}>
                      {DIAS.map((d,i) => {
                        const activo = (schedule.dias_busqueda||[]).includes(i);
                        const bloqueado = !activo && (schedule.dias_busqueda||[]).length >= MAX_DIAS_PLAN;
                        return (
                        <button key={i} onClick={() => toggleDia("dias_busqueda", i)} style={{
                          width:36, height:36, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.15)",
                          cursor: bloqueado ? "not-allowed" : "pointer", fontSize:12, fontWeight:600,
                          background: activo ? empColor : "transparent",
                          color: activo ? "#0F0A00" : bloqueado ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.5)",
                          opacity: bloqueado ? 0.5 : 1,
                        }}>{d}</button>
                      );})}
                    </div>
                  </div>

                  <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:4 }}>3. Configuración de email</div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:14 }}>Desde dónde salen los emails. SMTP detectado automáticamente.</div>

                    {/* Selector proveedor */}
                    <div style={{ marginBottom:12 }}>
                      <label style={sLabel}>PROVEEDOR</label>
                      <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                        {PROVEEDORES_SMTP.map(p => {
                          const esOtro = p.label==="Otro / manual" && !PROVEEDORES_SMTP.slice(0,-1).find(x=>x.host===smtpConfig.host);
                          const sel = smtpConfig.host===p.host || esOtro;
                          return (
                            <button key={p.label} onClick={() => setSmtpConfig(prev => ({...prev, host:p.host, port:p.port, modo:p.modo}))}
                              style={{ padding:"4px 12px", borderRadius:20, fontSize:12, cursor:"pointer", fontWeight:500,
                                border: sel ? "none" : "1px solid rgba(255,255,255,0.15)",
                                background: sel ? empColor : "transparent",
                                color: sel ? "#0F0A00" : "rgba(255,255,255,0.45)",
                              }}>{p.label}</button>
                          );
                        })}
                      </div>
                    </div>

                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:12 }}>
                      <div>
                        <label style={sLabel}>Email remitente</label>
                        <input type="email" value={smtpConfig.email||""} placeholder="email@empresa.com"
                          onChange={e => { setSmtpConfig(p=>({...p,email:e.target.value})); setSmtpStatus(null); }} style={sInput} />
                      </div>
                      <div>
                        <label style={sLabel}>Contraseña</label>
                        <input type="password" value={smtpConfig.password||""} placeholder="••••••••••••"
                          onChange={e => { setSmtpConfig(p=>({...p,password:e.target.value})); setSmtpStatus(null); }} style={sInput} />
                        {smtpConfig.host==="smtp.gmail.com" && (
                          <div style={{ marginTop:6, padding:"8px 10px", borderRadius:7, background:"rgba(234,179,8,0.08)", border:"1px solid rgba(234,179,8,0.25)", fontSize:11, color:"#FCD34D", lineHeight:1.5 }}>
                            Gmail requiere una contraseña de aplicación, no tu contraseña normal.
                          </div>
                        )}
                      </div>
                      <div>
                        <label style={sLabel}>Nombre del remitente</label>
                        <input value={smtpConfig.from_name||""} placeholder="Nombre — Empresa"
                          onChange={e => setSmtpConfig(p=>({...p,from_name:e.target.value}))} style={sInput} />
                      </div>
                      <div>
                        <label style={sLabel}>Reply-To</label>
                        <input value={smtpConfig.reply_to||""} placeholder="respuestas@empresa.com"
                          onChange={e => setSmtpConfig(p=>({...p,reply_to:e.target.value}))} style={sInput} />
                      </div>
                      {!PROVEEDORES_SMTP.slice(0,-1).find(p=>p.host===smtpConfig.host) && (
                        <>
                          <div>
                            <label style={sLabel}>Servidor SMTP</label>
                            <input value={smtpConfig.host||""} placeholder="mail.tudominio.com"
                              onChange={e => setSmtpConfig(p=>({...p,host:e.target.value}))} style={sInput} />
                          </div>
                          <div>
                            <label style={sLabel}>Puerto</label>
                            <input value={smtpConfig.port||""} placeholder="587"
                              onChange={e => setSmtpConfig(p=>({...p,port:e.target.value}))} style={sInput} />
                          </div>
                        </>
                      )}
                    </div>

                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <button onClick={probarSmtp} style={sBtn(false)}>{smtpStatus==="testing" ? "Probando..." : "Probar (lead 9999)"}</button>
                      <button onClick={guardarSmtp} style={sBtn(true)}>{smtpStatus==="saving" ? "Guardando..." : "Guardar"}</button>
                      {smtpStatus==="ok" && <span style={{ fontSize:12, color:"#10B981" }}>OK</span>}
                      {smtpStatus==="error" && <span style={{ fontSize:12, color:"#EF4444" }}>Revisá credenciales</span>}
                    </div>
                  </div>

                  {/* SECCIÓN 4: USUARIOS */}
                  <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:16, marginTop:16 }}>
                    <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:4 }}>4. Usuarios</div>
                    <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:4 }}>Cada usuario extra entra con su nombre y un PIN de 4 dígitos.</div>
                    <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:12, fontStyle:"italic" }}>Plan base: 1 admin + hasta {MAX_VENDEDORES_PLAN - 1} usuarios extra.</div>

                    <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:12 }}>
                      {vendedores.map((v,i) => (
                        <div key={v.id} style={{
                          display:"flex", alignItems:"center", justifyContent:"space-between",
                          padding:"8px 10px", border:"1px solid rgba(255,255,255,0.1)", borderRadius:6,
                        }}>
                          <span style={{ fontSize:13, color:"#fff" }}>
                            {v.nombre} {v.rol==="admin" && <span style={{ fontSize:10, color:"rgba(255,255,255,0.4)" }}>(admin)</span>}
                          </span>
                          <span style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontFamily:"monospace", fontSize:12, color:"rgba(255,255,255,0.5)" }}>PIN {v.pin}</span>
                            {i > 0 && (
                              <button onClick={() => eliminarVendedor(v.id)}
                                style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:12 }}>
                                Eliminar
                              </button>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>

                    {vendedores.length < MAX_VENDEDORES_PLAN ? (
                      <div style={{ display:"flex", gap:8 }}>
                        <input value={nuevoVendedorNombre} onChange={e => setNuevoVendedorNombre(e.target.value)}
                          placeholder="Nombre del nuevo usuario" style={{ ...sInput, flex:1 }} />
                        <button onClick={agregarVendedor} style={sBtn(true)}>+ Agregar</button>
                      </div>
                    ) : (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", fontStyle:"italic" }}>
                        Llegaste al límite de usuarios de tu plan. Para sumar más, hay que pasar a un plan superior.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* FILA 2: CONOCIMIENTO (ancho completo) */}
              <div style={sCard}>
                <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginBottom:4 }}>5. Conocimiento del agente</div>
                <div style={{ fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:16 }}>El "alma" del agente: quién sos, qué ofrecés y cómo calificar un buen lead.</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:14 }}>
                  {[
                    { icon:"🏢", label:"Perfil de empresa",    desc:"Quiénes son, qué ofrecen" },
                    { icon:"💰", label:"Lista de precios",      desc:"Precios por tipo de servicio" },
                    { icon:"❓", label:"Objeciones frecuentes", desc:"Cómo responder las comunes" },
                    { icon:"📋", label:"Protocolo de ventas",   desc:"Cómo trabajar cada oportunidad" },
                  ].map(item => (
                    <div key={item.label} style={{ display:"flex", flexDirection:"column", gap:8, padding:"14px", background:"rgba(255,255,255,0.02)", borderRadius:10, border:"1px solid rgba(255,255,255,0.07)" }}>
                      <span style={{ fontSize:22 }}>{item.icon}</span>
                      <div>
                        <div style={{ fontSize:13, fontWeight:600, color:"#fff", marginBottom:2 }}>{item.label}</div>
                        <div style={{ fontSize:11, color:"rgba(255,255,255,0.4)" }}>{item.desc}</div>
                      </div>
                      <label style={{ marginTop:"auto", padding:"6px 12px", borderRadius:7, border:`1px solid ${empColor}40`, background:`${empColor}10`, color:empColor, fontSize:11, cursor:"pointer", fontWeight:600, textAlign:"center" }}>
                        Subir archivo
                        <input type="file" accept=".pdf,.docx,.xlsx,.txt,.csv" style={{ display:"none" }}
                          onChange={e => {
                            const file = e.target.files[0];
                            if (!file) return;
                            const reader = new FileReader();
                            reader.onload = ev => {
                              fetch(`https://qzsrl.velqo.io/api/knowledge/${configEmpresa}`, {
                                method:"POST",
                                headers:{ "Content-Type":"application/octet-stream", "X-Filename":file.name },
                                body: ev.target.result,
                              }).then(r=>r.json()).then(d => {
                                if (d.ok) alert(`"${file.name}" procesado`);
                                else alert(`Error: ${d.error}`);
                              }).catch(() => alert("Error al subir"));
                            };
                            reader.readAsArrayBuffer(file);
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  ))}
                </div>
                <KnowledgeViewer empresa={configEmpresa} />
              </div>
            </div>
          );
        })()}

        {/* ── STATUS SISTEMA ── */}
        {tab === "dashboard" && sysStatus && (
          <div style={{ marginTop:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{
                  width:8, height:8, borderRadius:"50%",
                  background: Object.values(sysStatus.procesos).every(p=>p.ok) ? "#10B981" : "#F59E0B",
                  boxShadow: "0 0 6px " + (Object.values(sysStatus.procesos).every(p=>p.ok) ? "#10B981" : "#F59E0B"),
                }}/>
                <span style={{ fontWeight:700, fontSize:13, color:"#fff" }}>
                  {Object.values(sysStatus.procesos).every(p=>p.ok) ? "Sistema operativo" : "Sistema con advertencias"}
                </span>
              </div>
              {sysStatus.ultima_actualizacion && (
                <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>
                  Ultima actualizacion: {sysStatus.ultima_actualizacion}
                </span>
              )}
            </div>
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {Object.entries(sysStatus.procesos).map(([nombre, estado]) => (
                <div key={nombre} style={{
                  display:"flex", alignItems:"center", gap:6, padding:"5px 12px", borderRadius:20,
                  background: estado.ok ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)",
                  border: "1px solid " + (estado.ok ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"),
                }}>
                  <span style={{ fontSize:10 }}>{estado.ok ? "✅" : "⚠️"}</span>
                  <span style={{ fontSize:11, color: estado.ok ? "#10B981" : "#F59E0B", fontWeight:600 }}>{nombre}</span>
                  {!estado.ok && estado.error && (
                    <span style={{ fontSize:9, color:"rgba(255,255,255,0.35)", maxWidth:120, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {estado.error.replace(/[^a-zA-Z0-9 ]/g,"").trim().slice(0,40)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ADMIN ── */}
        {tab === "admin" && esAdmin && (
          <div>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontWeight:800, fontSize:18, color:"#fff", marginBottom:4 }}>Panel de Control</div>
              <div style={{ fontSize:12, color:"rgba(255,255,255,0.35)" }}>Análisis visual del pipeline completo</div>
            </div>

            {/* Fila 1 — Pie charts */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:16, marginBottom:16 }}>

              {/* Pipeline por empresa */}
              {(() => {
                const empresas = ["QZ SRL","Quick Clean","Air Quality"];
                const cols = ["#FF6B00","#F59E0B","#06B6D4"];
                const data = empresas.map((e,i) => ({
                  label: e, color: cols[i],
                  total: leads.filter(l=>l.empresa_captio===e).length,
                  monto: leads.filter(l=>l.empresa_captio===e&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                }));
                const totalLeads = data.reduce((a,b)=>a+b.total,0) || 1;
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#fff", marginBottom:16 }}>Pipeline por empresa</div>
                    <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
                      <svg width="140" height="140" viewBox="0 0 140 140">
                        {(() => {
                          let angle = -Math.PI/2;
                          return data.map((d,i) => {
                            const pct = d.total/totalLeads;
                            const endAngle = angle + pct*2*Math.PI;
                            const x1 = 70 + 60*Math.cos(angle), y1 = 70 + 60*Math.sin(angle);
                            const x2 = 70 + 60*Math.cos(endAngle), y2 = 70 + 60*Math.sin(endAngle);
                            const large = pct > 0.5 ? 1 : 0;
                            const path = pct > 0.999 
                              ? `M70,10 A60,60 0 1,1 69.99,10 Z`
                              : `M70,70 L${x1},${y1} A60,60 0 ${large},1 ${x2},${y2} Z`;
                            const el = <path key={i} d={path} fill={d.color} opacity={0.85}/>;
                            angle = endAngle;
                            return el;
                          });
                        })()}
                        <circle cx="70" cy="70" r="35" fill="#0F0F0F"/>
                        <text x="70" y="66" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold">{totalLeads}</text>
                        <text x="70" y="80" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="9">leads</text>
                      </svg>
                    </div>
                    {data.map(d => (
                      <div key={d.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:d.color, flexShrink:0 }}/>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.65)", flex:1 }}>{d.label}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                        {d.monto>0 && <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{fmt$(d.monto)}</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Estado del pipeline */}
              {(() => {
                const estados = [
                  { id:"Nuevo",    color:"#6B7280" },
                  { id:"Validado", color:"#3B82F6" },
                  { id:"Cotizado", color:"#F59E0B" },
                  { id:"Ganado",   color:"#10B981" },
                  { id:"Perdido",  color:"#EF4444" },
                ];
                const data = estados.map(e => ({
                  ...e,
                  total: leads.filter(l=>l.estado===e.id).length,
                  monto: leads.filter(l=>l.estado===e.id&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                })).filter(d=>d.total>0);
                const totalLeads = data.reduce((a,b)=>a+b.total,0) || 1;
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#fff", marginBottom:16 }}>Estado del pipeline</div>
                    <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
                      <svg width="140" height="140" viewBox="0 0 140 140">
                        {(() => {
                          let angle = -Math.PI/2;
                          return data.map((d,i) => {
                            const pct = d.total/totalLeads;
                            const endAngle = angle + pct*2*Math.PI;
                            const x1 = 70 + 60*Math.cos(angle), y1 = 70 + 60*Math.sin(angle);
                            const x2 = 70 + 60*Math.cos(endAngle), y2 = 70 + 60*Math.sin(endAngle);
                            const large = pct > 0.5 ? 1 : 0;
                            const path = pct > 0.999
                              ? `M70,10 A60,60 0 1,1 69.99,10 Z`
                              : `M70,70 L${x1},${y1} A60,60 0 ${large},1 ${x2},${y2} Z`;
                            const el = <path key={i} d={path} fill={d.color} opacity={0.85}/>;
                            angle = endAngle;
                            return el;
                          });
                        })()}
                        <circle cx="70" cy="70" r="35" fill="#0F0F0F"/>
                        <text x="70" y="66" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold">{leads.filter(l=>l.estado==="Ganado").length}</text>
                        <text x="70" y="80" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="9">ganados</text>
                      </svg>
                    </div>
                    {data.map(d => (
                      <div key={d.id} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:d.color, flexShrink:0 }}/>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.65)", flex:1 }}>{d.id}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                        {d.monto>0 && <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{fmt$(d.monto)}</span>}
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Leads por región */}
              {(() => {
                const ciudades = ["CABA","Córdoba","Rosario","Mendoza","Santa Fe"];
                const cols = ["#8B5CF6","#FF6B00","#06B6D4","#10B981","#F59E0B","#4B5563"];
                const data = [
                  ...ciudades.map((c,i) => ({
                    label: c, color: cols[i],
                    total: leads.filter(l=>l.region===c||l.ciudad?.includes(c)).length,
                  })),
                  { label:"Otros", color:"#4B5563", total: leads.filter(l=>!ciudades.some(c=>l.region===c||l.ciudad?.includes(c))).length },
                ].filter(d=>d.total>0);
                const totalLeads = data.reduce((a,b)=>a+b.total,0) || 1;
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#fff", marginBottom:16 }}>Leads por región</div>
                    <div style={{ display:"flex", justifyContent:"center", marginBottom:16 }}>
                      <svg width="140" height="140" viewBox="0 0 140 140">
                        {(() => {
                          let angle = -Math.PI/2;
                          return data.map((d,i) => {
                            const pct = d.total/totalLeads;
                            const endAngle = angle + pct*2*Math.PI;
                            const x1 = 70 + 60*Math.cos(angle), y1 = 70 + 60*Math.sin(angle);
                            const x2 = 70 + 60*Math.cos(endAngle), y2 = 70 + 60*Math.sin(endAngle);
                            const large = pct > 0.5 ? 1 : 0;
                            const path = pct > 0.999
                              ? `M70,10 A60,60 0 1,1 69.99,10 Z`
                              : `M70,70 L${x1},${y1} A60,60 0 ${large},1 ${x2},${y2} Z`;
                            const el = <path key={i} d={path} fill={d.color} opacity={0.85}/>;
                            angle = endAngle;
                            return el;
                          });
                        })()}
                        <circle cx="70" cy="70" r="35" fill="#0F0F0F"/>
                        <text x="70" y="66" textAnchor="middle" fill="#fff" fontSize="16" fontWeight="bold">{totalLeads}</text>
                        <text x="70" y="80" textAnchor="middle" fill="rgba(255,255,255,0.4)" fontSize="9">total</text>
                      </svg>
                    </div>
                    {data.map(d => (
                      <div key={d.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                        <div style={{ width:10, height:10, borderRadius:"50%", background:d.color, flexShrink:0 }}/>
                        <span style={{ fontSize:12, color:"rgba(255,255,255,0.65)", flex:1 }}>{d.label}</span>
                        <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                        <span style={{ fontSize:10, color:"rgba(255,255,255,0.3)" }}>{Math.round(d.total/totalLeads*100)}%</span>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>

            {/* Fila 2 — Segmentos + Cotizaciones */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>

              {/* Por segmento */}
              {(() => {
                const segs = Array.from(new Set(leads.map(l=>l.segmento).filter(Boolean)));
                const segCols = ["#8B5CF6","#FF6B00","#06B6D4","#10B981","#F59E0B","#EF4444","#3B82F6","#EC4899"];
                const data = segs.map((s,i) => ({
                  label: s, color: segCols[i%8],
                  total: leads.filter(l=>l.segmento===s).length,
                  ganados: leads.filter(l=>l.segmento===s&&l.estado==="Ganado").length,
                  monto: leads.filter(l=>l.segmento===s&&l.monto>0).reduce((a,b)=>a+b.monto,0),
                })).sort((a,b)=>b.total-a.total);
                const max = Math.max(...data.map(d=>d.total),1);
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#fff", marginBottom:16 }}>Leads por segmento / industria</div>
                    {data.map(d => (
                      <div key={d.label} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                          <span style={{ fontSize:12, color:"rgba(255,255,255,0.7)" }}>{d.label}</span>
                          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                            {d.ganados>0 && <span style={{ fontSize:10, color:"#10B981" }}>✓ {d.ganados} ganados</span>}
                            {d.monto>0 && <span style={{ fontSize:10, color:"#F59E0B" }}>{fmt$(d.monto)}</span>}
                            <span style={{ fontSize:12, fontWeight:700, color:d.color }}>{d.total}</span>
                          </div>
                        </div>
                        <div style={{ height:6, background:"rgba(255,255,255,0.06)", borderRadius:3 }}>
                          <div style={{ height:"100%", width:`${Math.round(d.total/max*100)}%`, background:d.color, borderRadius:3 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Cotizaciones por monto */}
              {(() => {
                const cotizados = leads.filter(l=>l.monto>0).sort((a,b)=>b.monto-a.monto).slice(0,8);
                const max = cotizados[0]?.monto || 1;
                const estCol = {Cotizado:"#F59E0B",Ganado:"#10B981",Perdido:"#EF4444"};
                return (
                  <div style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:20 }}>
                    <div style={{ fontWeight:700, fontSize:13, color:"#fff", marginBottom:4 }}>Top cotizaciones por monto</div>
                    <div style={{ fontSize:10, color:"rgba(255,255,255,0.3)", marginBottom:16 }}>
                      Total cotizado: {fmt$(leads.filter(l=>l.monto>0).reduce((a,b)=>a+b.monto,0))}
                    </div>
                    {cotizados.length === 0 && (
                      <div style={{ fontSize:12, color:"rgba(255,255,255,0.3)", textAlign:"center", padding:"20px 0" }}>
                        Aún no hay cotizaciones registradas
                      </div>
                    )}
                    {cotizados.map(l => (
                      <div key={l.id} style={{ marginBottom:12 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                          <span style={{ fontSize:11, color:"rgba(255,255,255,0.65)", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{l.empresa}</span>
                          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                            <span style={{ fontSize:9, color:estCol[l.estado]||"#6B7280", background:`${estCol[l.estado]||"#6B7280"}20`, padding:"1px 6px", borderRadius:10 }}>{l.estado}</span>
                            <span style={{ fontSize:12, fontWeight:700, color:"#F59E0B" }}>{fmt$(l.monto)}</span>
                          </div>
                        </div>
                        <div style={{ height:5, background:"rgba(255,255,255,0.06)", borderRadius:2 }}>
                          <div style={{ height:"100%", width:`${Math.round(l.monto/max*100)}%`, background:estCol[l.estado]||"#6B7280", borderRadius:2 }}/>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

            </div>
          </div>
        )}
      </div>

      {/* MODALES */}
      {modal?.tipo === "mover" && (
        <ModalMover
          lead={modal.lead}
          destino={modal.destino}
          onConfirm={confirmarMover}
          onCancel={() => setModal(null)}
        />
      )}
      {modal?.tipo === "detalle" && (
        <ModalDetalle
          lead={modal.lead}
          vendedores={vendedores}
          onClose={() => setModal(null)}
          onAsignar={asignarVendedor}
          esAdmin={esAdmin}
          usuarioActual={usuario}
        />
      )}
    </div>
  );
}
